import { useState, useEffect, useMemo, useRef } from 'react';
import { Timestamp, deleteField } from 'firebase/firestore';
import {
  getWatchBrands,
  getWatchBrandById,
  getWatchBrandsByIds,
  getDisabledWatchBrands,
  getLegacyShopifyWatchBrands,
  getWatchBrandsWithAcquisitions,
  updateWatchBrand,
  createWatchBrand,
  getReferencesForBrand,
  enrichBrandWithAI,
  fetchViaCloudflareProxy,
  resolveLogoToStorage,
  resolveHeroToStorage,
  LogoFetchError,
  getAllBrandImportSources,
  brandHasShopifySource,
} from '../../services/firebase';
import {
  fetchBrandListing,
  isAlgoliaConfigured,
  brandHitToWatchBrand,
  mergeBrandDocs,
  CEASED_BRAND_STATUSES,
} from '../../services/algolia';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { EnrichHintDialog } from '../common/EnrichHintDialog';
import { FormSection } from '../common/FormSection';
import { scraperBrandDisplayNames } from '../../services/extractorMatching';
import {
  fetchBrandOverrides,
  fetchScrapers,
  type ScraperDescriptor,
} from '../../services/extractorService';
import { Button } from '../common/Button';
import { BrandTimelineSection } from './BrandTimelineSection';
import {
  BrandCommerceFields,
  applySeedToCommerceForm,
  brandCommerceCreatePayload,
  brandCommerceFormFromBrand,
  brandCommerceUpdatePayload,
  emptyBrandCommerceForm,
  type BrandCommerceForm,
} from './BrandCommerceFields';
import {
  BrandIdentityFields,
  brandIdentityCreatePayload,
  brandIdentityFormFromBrand,
  brandIdentityUpdatePayload,
  emptyBrandIdentityForm,
  type BrandIdentityForm,
} from './BrandIdentityFields';
import { brandEnrichHints, buildBrandAiHandlers } from './brandAiHandlers';
import {
  BrandLocationFields,
  LOCATION_TEXT_KEYS,
  brandLocationCreatePayload,
  brandLocationFormFromBrand,
  brandLocationUpdatePayload,
  emptyBrandLocationForm,
  type BrandLocationForm,
} from './BrandLocationFields';
import {
  CREATE_FORM_KEYS,
  EDIT_FORM_KEYS,
  buildSeedWritePayload,
  describeCarriedValues,
  type CreateBrandSeed,
  type EditBrandPatchSeed,
} from './brandSeeds';
import { Modal } from '../common/Modal';
import { CopyLinkButton } from '../common/CopyLinkButton';
import { buildShareUrl } from '../../services/shareLinks';
import { BrandDeleteModal } from './BrandDeleteModal';
import { Badge } from '../common/Badge';
import { StatusBadge } from '../common/StatusBadge';
import {
  brandHasSquareOnlineSource,
  brandHasWooCommerceSource,
} from '../../services/importSourceSignals';
import { AcquisitionsEditor } from '../common/AcquisitionsEditor';
import { DocsLinksEditor } from '../common/DocsLinksEditor';
import { BrandImageUpload } from '../common/BrandImageUpload';
import {
  CountrySelect,
  COUNTRY_FLAGS,
  getCountryDisplay,
} from '../common/CountrySelect';
import { ParentOrgPicker } from '../ParentOrganizations/ParentOrgPicker';
import { useParentOrgIndex } from '../ParentOrganizations/useParentOrgIndex';
import {
  matchesParentOrgFilter,
  orgDisplayName,
  parentOrgChips,
  parentOrgFromText,
  resolutionLabel,
  resolveParentOrgForBrand,
  resolvedOrg,
  UNSET_PARENT_ORG_FILTER,
  type ParentOrgIndex,
} from '../ParentOrganizations/parentOrgResolve';
import { TagInput } from './TagInput';
import { LookupMultiSelect } from './LookupMultiSelect';
import type {
  WatchBrand,
  BrandStatus,
  ImportSourceConfig,
  BrandAcquisition,
  DocsLink,
} from '../../types';
import { cleanDocsLinks } from '../../utils/docsLinks';
import { brandShowsProductLinks } from '../../services/productLinks';
import { invalidateDocsLinks } from '../../hooks/useDocsLinks';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import { useRelevanceSort } from '../../hooks/useRelevanceSort';
import { RELEVANCE_SORT, type WithRelevance } from '../../services/relevanceSort';
import { RelevanceSortToggle } from '../common/RelevanceSortToggle';
import toast from 'react-hot-toast';
import {
  countEntityLinks,
  describeLinkLoss,
  withholdLinkLosingDescriptions,
} from '../../services/descriptionLinkGuard';
import { RichTextEditor } from '../common/RichTextEditor';
import {
  htmlToPlainText,
  isEmptyRichText,
  richDescriptionPatch,
  richDescriptionSeed,
} from '../../services/descriptionHtml';

export type BrandSortField = 'name' | 'refs' | 'createdAt' | 'country' | 'lastImportedAt';
export type BrandSortDir = 'asc' | 'desc';

/** The sort the listing opens on, and the one relevance hands back to. */
const DEFAULT_BRAND_SORT: BrandSortField = 'name';
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Soft target for glyphDescription — the counter turns amber past this, but a
 * longer description still saves. Brands with several distinct glyphs need the
 * room.
 */
const GLYPH_DESCRIPTION_SOFT_LIMIT = 200;

/**
 * Shown on the Refs column and the ref-derived stat pills. The listing a brand
 * opens into is parents-only, and these numbers follow it — worth saying out
 * loud, because a variant-heavy brand's stored doc count is far higher.
 */
const REFS_TOOLTIP =
  'Parents and standalone references only — variants are excluded, matching the reference listing.';

export interface BrandListState {
  searchTerm: string;
  /**
   * Widened to admit `'relevance'`, which is not a column — see
   * `services/relevanceSort.ts`. Every read of it in a comparator or a header
   * highlight goes through `effectiveSortField` instead, so the sentinel never
   * reaches code that expects a brand property name.
   */
  sortField: WithRelevance<BrandSortField>;
  sortDir: BrandSortDir;
  currentPage: number;
  pageSize: number;
  hideZeroRefs: boolean;
  shopifyOnly: boolean;
  selectedContinent: string | null;
  selectedCountry: string | null;
  selectedParentOrg: string | null;
}

export const defaultBrandListState: BrandListState = {
  searchTerm: '',
  sortField: DEFAULT_BRAND_SORT,
  sortDir: 'asc',
  currentPage: 1,
  pageSize: 25,
  hideZeroRefs: false,
  shopifyOnly: false,
  selectedContinent: null,
  selectedCountry: null,
  selectedParentOrg: null,
};

interface BrandEditorProps {
  onSelectBrand: (brand: WatchBrand) => void;
  listState: BrandListState;
  onListStateChange: (state: BrandListState) => void;
  editBrandRequest?: WatchBrand | null;
  onEditBrandComplete?: (updatedBrand?: WatchBrand) => void;
  updatedBrand?: WatchBrand | null;
  /**
   * One-shot: open the Create modal pre-filled from an external catalogue.
   * Consumed (and cleared by the parent) as soon as the modal is open; the
   * editor keeps its own copy until the modal saves or closes.
   */
  createBrandSeed?: CreateBrandSeed | null;
  onCreateBrandSeedConsumed?: () => void;
  /** The seeded brand was created. Not called when the modal is cancelled. */
  onCreateBrandSeedApplied?: (brandId: string, seed: CreateBrandSeed) => void;
  /**
   * One-shot: open the Edit modal for `brandId` with the seed's values laid
   * over the stored document. Same consume-once contract as `createBrandSeed`.
   */
  editBrandPatchSeed?: EditBrandPatchSeed | null;
  onEditBrandPatchSeedConsumed?: () => void;
  /** The seeded edit was saved. Not called when the modal is cancelled. */
  onEditBrandPatchSeedApplied?: (brandId: string, seed: EditBrandPatchSeed) => void;
  /**
   * A seeded Create or Edit modal was closed WITHOUT saving. Lets the surface
   * that sent the seed take the reviewer back to where they were.
   */
  onBrandSeedCancelled?: (seed: CreateBrandSeed | EditBrandPatchSeed) => void;
}

const CONTINENT_MAP: Record<string, { emoji: string; countries: string[] }> = {
  'Europe': {
    emoji: '🌍',
    countries: ['Switzerland', 'Germany', 'France', 'United Kingdom', 'Italy', 'Denmark', 'Sweden', 'Finland', 'Norway', 'Russia', 'Spain', 'Netherlands', 'Austria', 'Czech Republic', 'Poland', 'Portugal', 'Belgium', 'Ireland', 'Greece', 'Hungary', 'Romania', 'Lithuania'],
  },
  'Asia': {
    emoji: '🌏',
    countries: ['Japan', 'China', 'Hong Kong', 'Singapore', 'South Korea', 'Taiwan', 'India', 'Israel', 'Thailand', 'Philippines', 'Indonesia', 'Vietnam', 'Lebanon', 'UAE'],
  },
  'Americas': {
    emoji: '🌎',
    countries: ['United States', 'Canada', 'Brazil', 'Mexico', 'Puerto Rico'],
  },
  'Africa': {
    emoji: '🌍',
    countries: ['South Africa', 'Nigeria', 'Kenya'],
  },
  'Oceania': {
    emoji: '🌏',
    countries: ['Australia', 'New Zealand'],
  },
};

const COUNTRY_TO_CONTINENT: Record<string, string> = Object.fromEntries(
  Object.entries(CONTINENT_MAP).flatMap(([continent, { countries }]) =>
    countries.map((c) => [c, continent]),
  ),
);

function formatRelativeTime(timestamp?: Timestamp): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Where an id-bound part of the brand form will appear once the brand is saved. */
function AfterSaveNote({ children }: { children: React.ReactNode }) {
  return (
    <p className='text-xs text-gray-500 italic border border-dashed border-gray-300 rounded-lg px-3 py-2'>
      {children}
    </p>
  );
}

export function BrandEditor({ onSelectBrand, listState, onListStateChange, editBrandRequest, onEditBrandComplete, updatedBrand, createBrandSeed, onCreateBrandSeedConsumed, onCreateBrandSeedApplied, editBrandPatchSeed, onEditBrandPatchSeedConsumed, onEditBrandPatchSeedApplied, onBrandSeedCancelled }: BrandEditorProps) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  /**
   * Per-brand count of PARENTS AND STANDALONES — not every stored doc. The
   * reference listing has been parents-only server-side since the `isVariant`
   * filter landed, so a total here would promise "234 refs" and then hand the
   * admin ~40 rows. Every stat, filter and sort on this page reads this map, so
   * they all agree with what opening the brand actually shows.
   *
   * Read straight off `WatchBrand.parentReferenceCount`, the server-maintained
   * counter written by watchlock's `maintainWatchBrandReferenceCount` trigger.
   * This used to be one `getCountFromServer` per brand — ~200 aggregation
   * queries on every page open, doubled again under Strict Mode. The rows are
   * already loaded, so the map costs nothing; it is derived rather than stored
   * so that a row replaced by a fresher document updates its count with it.
   *
   * The counter is carried by BOTH sources the listing loads from — the Algolia
   * hit (watchlock #863 indexes it) and the Firestore document — so the map is
   * complete either way.
   *
   * A brand with NO counter yet (pre-dates the field and hasn't been swept by
   * `recomputeAllWatchBrandReferenceCounts`) is absent from this map rather than
   * present at 0 — `undefined` means "not computed", which is what lets the row
   * badge render "..." and keeps Delete enabled for it. Never coerce it.
   *
   * The delete guard deliberately does NOT use it — deleting a brand whose refs
   * are all variants would orphan them. The server re-reads the true total.
   */
  /** The brand whose delete confirmation is open. */
  const [deletingBrand, setDeletingBrand] = useState<WatchBrand | null>(null);
  const parentRefCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const brand of brands) {
      if (typeof brand.parentReferenceCount === 'number') {
        counts[brand.id] = brand.parentReferenceCount;
      }
    }
    return counts;
  }, [brands]);
  /**
   * Ids in `brands` whose entry is a full Firestore document rather than an
   * Algolia projection. Everything in `BRAND_FIELDS_NOT_INDEXED` — the Website
   * column, the ⚘ Acquisitions badge, `yearCeased`, the legacy Shopify fields —
   * is only trustworthy for these, and the edit form only ever opens on one.
   */
  const [hydratedIds, setHydratedIds] = useState<Set<string>>(new Set());
  /**
   * Ids already sent to `getWatchBrandsByIds`, successful or not. Separate from
   * `hydratedIds` and held in a ref so a failed read is not retried on every
   * render of the same page — a render-loop's worth of Firestore reads.
   */
  const requestedHydration = useRef<Set<string>>(new Set());
  /**
   * True when the whole collection came from Firestore because Algolia could not
   * answer. Every row is then already a full document, so hydration is skipped
   * outright rather than re-reading what we hold.
   */
  const [loadedFromFirestore, setLoadedFromFirestore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  // Canonical Shopify status comes from each brand's import-sources doc (a
  // source of kind 'shopify'), not the legacy shopifyEnabled/shopifyUrl fields
  // which can drift when a source is removed via the Import Sources page. Loaded
  // once on mount; brands with no doc fall back to the legacy fields.
  const [sourcesByBrand, setSourcesByBrand] = useState<Map<
    string,
    ImportSourceConfig[]
  > | null>(null);

  const { searchTerm, sortField, sortDir, currentPage, pageSize, hideZeroRefs, shopifyOnly, selectedContinent, selectedCountry, selectedParentOrg } = listState;

  const updateListState = (updates: Partial<BrandListState>) => {
    onListStateChange({ ...listState, ...updates });
  };
  const [editingBrand, setEditingBrand] = useState<WatchBrand | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  /**
   * The external-import seed behind the open Create / Edit modal, if any. The
   * props are one-shot and cleared by the parent the moment the modal opens, so
   * the save path reads these copies. Each is dropped when its modal saves or
   * closes — a seed must never outlive the modal it opened and leak its stamps
   * into an unrelated save.
   */
  const [activeCreateSeed, setActiveCreateSeed] = useState<CreateBrandSeed | null>(null);
  const [activeEditSeed, setActiveEditSeed] = useState<EditBrandPatchSeed | null>(null);

  // Update local brand list when a brand is updated externally (e.g., bulk
  // import). What arrives is a full document, so the row it replaces stops being
  // an Algolia projection and needs no hydration read.
  useEffect(() => {
    if (updatedBrand) {
      setBrands((prev) =>
        prev.map((b) => (b.id === updatedBrand.id ? updatedBrand : b)),
      );
      setHydratedIds((prev) => new Set(prev).add(updatedBrand.id));
      requestedHydration.current.add(updatedBrand.id);
    }
  }, [updatedBrand]);

  // Brand form state — ONE set for both modes. Create mode is `isCreating`
  // with no `editingBrand`; once the brand is saved the same form carries on in
  // edit mode, which is when the id-bound parts (images, timeline) appear.
  const [editName, setEditName] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  // Rich description (HTML → `descriptionRich`, plain text → `description`).
  // The seed is what the editor opened with, so an untouched editor writes nothing.
  const [editDescription, setEditDescription] = useState('');
  const [editDescriptionSeed, setEditDescriptionSeed] = useState('');
  const [editCountry, setEditCountry] = useState('');
  // Country-verified lock + city / state-region / assembly / design location.
  const [editLocation, setEditLocation] = useState<BrandLocationForm>(emptyBrandLocationForm);
  // Shopify storefront flag, ecommerce platform, Instagram URL and founder.
  const [editCommerce, setEditCommerce] = useState<BrandCommerceForm>(emptyBrandCommerceForm);
  // Ownership type, and for a revived brand its revival year and predecessor.
  const [editIdentity, setEditIdentity] = useState<BrandIdentityForm>(emptyBrandIdentityForm);
  const [editWebsite, setEditWebsite] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editHeroImage, setEditHeroImage] = useState('');
  const [editHeroImageSourceUrl, setEditHeroImageSourceUrl] = useState('');
  const [editUseHeroImage, setEditUseHeroImage] = useState(false);
  const [editLogoHeroTransparent, setEditLogoHeroTransparent] = useState(false);
  const [editLogo, setEditLogo] = useState('');
  const [editLogoSourceUrl, setEditLogoSourceUrl] = useState('');
  // `undefined` = not stated. An existing brand loads as a plain boolean; a new
  // one starts undefined, so an untouched box is not written as false.
  const [editIsMicroBrand, setEditIsMicroBrand] = useState<boolean | undefined>(false);
  const [editDedupIdentityField, setEditDedupIdentityField] =
    useState<'reference' | 'model'>('reference');
  const [editIsPromoted, setEditIsPromoted] = useState(false);
  const [editPromotedOrder, setEditPromotedOrder] = useState(0);
  const [editIsDisabled, setEditIsDisabled] = useState(false);
  const [editStatus, setEditStatus] = useState<BrandStatus>('active');
  const [editNeedsReview, setEditNeedsReview] = useState(false);
  const [editHideImages, setEditHideImages] = useState(false);
  // Defaults to true (links shown) for new/unset brands; only explicit false hides.
  const [editShowProductLinks, setEditShowProductLinks] = useState(true);
  const [mbwdbChecking, setMbwdbChecking] = useState(false);
  const [mbwdbResult, setMbwdbResult] = useState<'found' | 'not_found' | null>(null);
  const [editInfo, setEditInfo] = useState('');
  const [editAltNames, setEditAltNames] = useState<string[]>([]);
  const [editAbbreviations, setEditAbbreviations] = useState<string[]>([]);
  const [editWindingDirections, setEditWindingDirections] = useState<string[]>([]);
  const [editFounded, setEditFounded] = useState('');
  const [editParentOrg, setEditParentOrg] = useState('');
  // Dual-written beside editParentOrg — see ParentOrgPicker.
  const [editParentOrgId, setEditParentOrgId] = useState('');
  const [editAcquisitions, setEditAcquisitions] = useState<BrandAcquisition[]>([]);
  const [editYearCeased, setEditYearCeased] = useState<number | undefined>(undefined);
  const [editDocs, setEditDocs] = useState<DocsLink[]>([]);
  const [editAiDescription, setEditAiDescription] = useState('');
  const [editGlyphDescription, setEditGlyphDescription] = useState('');
  const [editGlyphImageUrl, setEditGlyphImageUrl] = useState('');
  const [glyphAiLoading, setGlyphAiLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  // The Enrich-with-AI hint dialog: open state and the curator's text, which
  // goes to this run only (never saved to the brand).
  const [showEnrichHint, setShowEnrichHint] = useState(false);
  const [enrichHint, setEnrichHint] = useState('');
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, unknown>>({});
  const [aiConfidence, setAiConfidence] = useState<Record<string, string>>({});
  const [aiAccepted, setAiAccepted] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadBrands();
  }, []);

  // Load every brand's import-sources doc once on mount so the Shopify pill,
  // row coloring, count and "Shopify only" filter can derive from the canonical
  // source kind rather than the legacy fields. The Square Online pill and row
  // tint and the WooCommerce pill and tint read the same map (no legacy fallback
  // exists for those). Non-fatal: on failure we fall
  // back to the legacy fields (sourcesByBrand stays null).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const map = await getAllBrandImportSources();
        if (!cancelled) setSourcesByBrand(map);
      } catch (error) {
        console.error('Failed to load brand import sources:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * The scraper catalogue, for the ⚡ Scraper badge on a brand's row.
   *
   * Same two reads the Run scraper button on `ReferenceBrowser` makes, and the
   * same gate: `fetchScrapers` is session-cached against its ETag and
   * `fetchBrandOverrides` is a promise-cached single document, so a listing that
   * an operator reaches after browsing a brand usually costs nothing at all.
   *
   * A failure is swallowed to an empty catalogue — no badges, which is what the
   * screen showed before this existed. An unreachable extractor is diagnosed by
   * Test connection on the config screen, not by a broken-looking brands table.
   */
  const extractorJobsEnabled = useFeatureFlag('tools.extractorJobs');
  const [scraperCatalogue, setScraperCatalogue] = useState<ScraperDescriptor[] | null>(null);
  const [scraperBrandOverrides, setScraperBrandOverrides] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!extractorJobsEnabled) return;
    let cancelled = false;
    void fetchBrandOverrides().then((map) => {
      if (!cancelled) setScraperBrandOverrides(map);
    });
    void fetchScrapers()
      .then((list) => {
        if (!cancelled) setScraperCatalogue(list);
      })
      .catch(() => {
        if (!cancelled) setScraperCatalogue([]);
      });
    return () => {
      cancelled = true;
    };
  }, [extractorJobsEnabled]);

  /**
   * `brandId → the modules that name it`, so each row is a map lookup rather
   * than a scan of the catalogue. Empty while the fetch is in flight, which is
   * simply a row with no badge yet.
   */
  const scraperNamesByBrand = useMemo(
    () => scraperBrandDisplayNames(scraperCatalogue, scraperBrandOverrides),
    [scraperCatalogue, scraperBrandOverrides],
  );

  /**
   * The listing itself, from Algolia — one search request in place of the
   * 561-document `getDocs(watchBrands)` this page used to run on every mount.
   *
   * Three Firestore top-ups ride along, all targeted rather than
   * collection-wide. Each covers something the index cannot answer and that is
   * read across the WHOLE catalogue rather than per visible row — anything
   * needed per-row is filled in by the hydration pass instead:
   *
   * - Disabled brands, which the indexer evicts from `watchkeeper_brands`
   *   entirely. Without this they would disappear from the only screen that can
   *   re-enable them.
   * - Brands still carrying the legacy `shopifyEnabled` flag, which the Shopify
   *   pill, the green row tint and the Last Import column fall back to when a
   *   brand has no import-sources document.
   * - Brands carrying `acquisitions` — the ⚘ badge, and the editor's warning
   *   that other brands claim this one as an acquisition. Best-effort: a failure
   *   here costs those two surfaces their completeness, not the load.
   *
   * The Firestore full-collection read survives as the FALLBACK. Algolia being
   * unconfigured or down must degrade this page to slow-and-complete, never to
   * empty — the same reasoning that kept the fallback in the reference listing.
   */
  const loadBrands = async () => {
    requestedHydration.current = new Set();
    try {
      if (isAlgoliaConfigured()) {
        try {
          const [hits, disabled, legacyShopify] = await Promise.all([
            fetchBrandListing(),
            getDisabledWatchBrands(),
            getLegacyShopifyWatchBrands(),
          ]);
          let docs = disabled.concat(legacyShopify);
          try {
            docs = docs.concat(await getWatchBrandsWithAcquisitions());
          } catch (error) {
            console.warn('Brand acquisitions pre-load unavailable', error);
          }
          setBrands(mergeBrandDocs(hits.map(brandHitToWatchBrand), docs));
          setHydratedIds(new Set(docs.map((b) => b.id)));
          setLoadedFromFirestore(false);
          setLoading(false);
          return;
        } catch (error) {
          console.warn(
            'Brand listing unavailable from Algolia, falling back to Firestore',
            error,
          );
        }
      }
      const data = await getWatchBrands();
      setBrands(data);
      setHydratedIds(new Set(data.map((b) => b.id)));
      setLoadedFromFirestore(true);
      setLoading(false);
    } catch (error) {
      console.error('Error loading brands:', error);
      toast.error('Failed to load brands');
      setLoading(false);
    }
  };

  /**
   * Replace a listing row with its Firestore document, if it isn't one already.
   *
   * The gate before every action that consumes fields the index doesn't carry:
   * opening the editor (which writes the whole form back, so an Algolia
   * projection would save blanks over real values) and opening a brand's
   * references (`ReferenceBrowser` reads `dedupIdentityField`, `hideImages` and
   * the rest). One document read, and only the first time per brand.
   */
  const ensureFullBrand = async (brand: WatchBrand): Promise<WatchBrand> => {
    if (loadedFromFirestore || hydratedIds.has(brand.id)) return brand;
    try {
      const full = await getWatchBrandById(brand.id);
      if (!full) return brand;
      setBrands((prev) => mergeBrandDocs(prev, [full]));
      setHydratedIds((prev) => new Set(prev).add(full.id));
      requestedHydration.current.add(full.id);
      return full;
    } catch (error) {
      console.error('Failed to load brand document:', error);
      toast.error(`Could not load "${brand.name}"`);
      return brand;
    }
  };

  // Stats. Both ref-derived pills are parents-only, so `Total refs` is exactly
  // the sum of the Refs column and `With refs` counts brands that have at least
  // one LISTABLE ref — a brand holding nothing but variants reads as empty here,
  // which is what the admin sees when they open it.
  const totalRefs = useMemo(
    () => Object.values(parentRefCounts).reduce((sum, c) => sum + c, 0),
    [parentRefCounts],
  );
  const shopifyCount = useMemo(
    () => brands.filter((b) => brandHasShopifySource(b, sourcesByBrand)).length,
    [brands, sourcesByBrand],
  );
  const brandsWithRefsCount = useMemo(
    () => brands.filter((b) => (parentRefCounts[b.id] ?? 0) > 0).length,
    [brands, parentRefCounts],
  );

  const continentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let unsetCount = 0;
    for (const brand of brands) {
      if (!brand.country) {
        unsetCount += 1;
        continue;
      }
      const continent = COUNTRY_TO_CONTINENT[brand.country];
      if (continent) {
        counts[continent] = (counts[continent] ?? 0) + 1;
      }
    }
    return { byContinent: counts, unset: unsetCount };
  }, [brands]);

  const countryCountsForContinent = useMemo(() => {
    if (!selectedContinent || selectedContinent === 'Unset') return {};
    const counts: Record<string, number> = {};
    const continentCountries = CONTINENT_MAP[selectedContinent]?.countries ?? [];
    for (const brand of brands) {
      if (brand.country && continentCountries.includes(brand.country)) {
        counts[brand.country] = (counts[brand.country] ?? 0) + 1;
      }
    }
    return counts;
  }, [brands, selectedContinent]);

  /**
   * The search box, matched by Algolia rather than by substring.
   *
   * `.includes()` over name/displayName/altNames/abbreviations demanded exact
   * spelling — "journe" missed F.P.Journe, "raul pages" missed Raúl PAGÈS, and
   * an org name like "LVMH" matched nothing at all. Algolia adds typo tolerance,
   * prefix matching, accent folding and parentOrg search, and falls back to the
   * same substring filter when it can't answer.
   *
   * Only MATCHING moves server-side; ORDER stays local, because this listing has
   * its own sort control and re-sorts whatever survives the filters anyway.
   *
   * `brands` is itself Algolia-derived now, but the search still merges against
   * it rather than replacing it: the merge is what keeps a brand the index has
   * dropped — a disabled one, or one created seconds ago — findable by an exact
   * substring, and what resolves each hit back to the row the page renders.
   *
   * The continent, country and parent-org counts below stay derived from the
   * full `brands` list rather than from Algolia facet counts. That is what makes
   * them disjunctive: each is computed over every loaded brand, so selecting
   * France narrows the ROWS without collapsing the country pills to France
   * alone. Facet counts would have to be re-queried once per dimension, each
   * omitting its own selection, to behave the same way.
   */
  const { results: searchedBrands, activeTerm } = useBrandSearch(brands, searchTerm);

  /**
   * Relevance sort. `searchedBrands` already arrives in Algolia's rank order, so
   * "relevance" here is the local re-sort below being SKIPPED rather than a
   * different query — see `services/relevanceSort.ts`.
   *
   * Keyed off `activeTerm` (the debounced term the results are for), not
   * `searchTerm`, so the switch happens with the ranked rows rather than a beat
   * before them.
   */
  const {
    relevanceActive,
    effectiveSortField,
    relevanceAvailable,
    pickSort,
    markUserPicked,
  } = useRelevanceSort<BrandSortField>({
    query: activeTerm,
    sortField,
    defaultSortField: DEFAULT_BRAND_SORT,
    setSortField: (field) => updateListState({ sortField: field, currentPage: 1 }),
  });

  // Parent organizations, read from `parent_organizations` — no longer the
  // lookup, which kept showing old names after an org was renamed. Loaded once
  // for the listing, the filter chips and both pickers; resolution is in memory.
  // Listing rows come from the brands index, which carries `parentOrgId`, so
  // they resolve by id and show the organization's own display name. The
  // legacy string is only the fallback for a brand that was never linked.
  const {
    index: parentOrgIndex,
    loading: parentOrgsLoading,
    error: parentOrgsError,
  } = useParentOrgIndex();

  const parentOrgCounts = useMemo(
    () => parentOrgChips(brands, parentOrgIndex),
    [brands, parentOrgIndex],
  );

  const processedBrands = useMemo(() => {
    let result = [...searchedBrands];

    // Filter zero refs — zero LISTABLE refs, so a variants-only brand is hidden
    // by this too. It has nothing an admin can open.
    //
    // A brand with NO counter yet is KEPT: `undefined` is "not computed", not
    // "empty", and hiding it would silently drop real brands from the listing
    // until the recompute sweep runs.
    if (hideZeroRefs) {
      result = result.filter((brand) => {
        const count = parentRefCounts[brand.id];
        return count === undefined || count > 0;
      });
    }

    // Filter Shopify enabled (canonical source kind, legacy fallback)
    if (shopifyOnly) {
      result = result.filter((brand) => brandHasShopifySource(brand, sourcesByBrand));
    }

    // Filter by geography (continent / country)
    if (selectedContinent === 'Unset') {
      result = result.filter((brand) => !brand.country);
    } else if (selectedCountry) {
      result = result.filter((brand) => brand.country === selectedCountry);
    } else if (selectedContinent) {
      const continentCountries = CONTINENT_MAP[selectedContinent]?.countries ?? [];
      result = result.filter(
        (brand) => brand.country && continentCountries.includes(brand.country),
      );
    }

    // Filter by parent organization — one chip per org, whatever spelling or FK
    // each brand holds (see parentOrgResolve).
    if (selectedParentOrg) {
      result = result.filter((brand) =>
        matchesParentOrgFilter(brand, selectedParentOrg, parentOrgIndex),
      );
    }

    // Sort — unless relevance is on, in which case the order Algolia ranked is
    // the answer. Every filter above is a `.filter`, so that order survives them
    // intact and there is nothing to restore.
    if (relevanceActive) return result;

    result.sort((a, b) => {
      let cmp = 0;
      if (effectiveSortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (effectiveSortField === 'refs') {
        cmp = (parentRefCounts[a.id] ?? 0) - (parentRefCounts[b.id] ?? 0);
      } else if (effectiveSortField === 'createdAt') {
        const aTime = a.createdAt?.toMillis?.() ?? 0;
        const bTime = b.createdAt?.toMillis?.() ?? 0;
        cmp = aTime - bTime;
      } else if (effectiveSortField === 'country') {
        cmp = (a.country ?? '').localeCompare(b.country ?? '');
      } else if (effectiveSortField === 'lastImportedAt') {
        const aTime = a.lastImportedAt?.toMillis?.() ?? 0;
        const bTime = b.lastImportedAt?.toMillis?.() ?? 0;
        cmp = aTime - bTime;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [searchedBrands, hideZeroRefs, shopifyOnly, sourcesByBrand, selectedContinent, selectedCountry, selectedParentOrg, parentOrgIndex, relevanceActive, effectiveSortField, sortDir, parentRefCounts]);

  const totalPages = Math.max(1, Math.ceil(processedBrands.length / pageSize));
  const pagedBrands = useMemo(
    () =>
      processedBrands.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [processedBrands, currentPage, pageSize],
  );

  /**
   * Fill in the visible page's full documents behind the rendered rows.
   *
   * The row paints from the Algolia hit immediately; the Website cell and the ⚘
   * Acquisitions badge appear a beat later, when the page's documents land. That
   * is one `documentId in` query per thirty rows — 25 reads for a default page,
   * against the 561 the whole collection cost on mount — and each brand is read
   * at most once per load, so paging back and forth is free.
   *
   * `requestedHydration` is marked BEFORE the read, not after: the effect
   * re-runs on every `brands` change (its own write included), so an id left
   * unmarked by a failed read would be re-requested on each pass.
   *
   * A page change deliberately does NOT cancel the read it leaves behind. The
   * result is additive — a document is always fresher than the hit it replaces,
   * whatever order they land in — and dropping it would strand those rows
   * unhydrated for the rest of the session, since their ids are already marked
   * as requested.
   */
  useEffect(() => {
    if (loadedFromFirestore) return;
    const ids = pagedBrands
      .map((b) => b.id)
      .filter((id) => !requestedHydration.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => requestedHydration.current.add(id));
    void (async () => {
      try {
        const docs = await getWatchBrandsByIds(ids);
        if (docs.length === 0) return;
        setBrands((prev) => mergeBrandDocs(prev, docs));
        setHydratedIds((prev) => {
          const next = new Set(prev);
          docs.forEach((d) => next.add(d.id));
          return next;
        });
      } catch (error) {
        console.warn('Brand row hydration failed', error);
      }
    })();
  }, [pagedBrands, loadedFromFirestore]);

  // Selection
  const allPageSelected = pagedBrands.length > 0 && pagedBrands.every((b) => selectedBrands.has(b.id));
  const somePageSelected = pagedBrands.some((b) => selectedBrands.has(b.id)) && !allPageSelected;

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedBrands((prev) => {
        const next = new Set(prev);
        pagedBrands.forEach((b) => next.delete(b.id));
        return next;
      });
    } else {
      setSelectedBrands((prev) => {
        const next = new Set(prev);
        pagedBrands.forEach((b) => next.add(b.id));
        return next;
      });
    }
  };

  const toggleSelect = (brandId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) next.delete(brandId);
      else next.add(brandId);
      return next;
    });
  };

  const clearSelection = () => setSelectedBrands(new Set());

  const selectedBrandsList = useMemo(
    () => brands.filter((b) => selectedBrands.has(b.id)),
    [brands, selectedBrands],
  );
  const selectedShopifyBrands = useMemo(
    () => selectedBrandsList.filter((b) => brandHasShopifySource(b, sourcesByBrand)),
    [selectedBrandsList, sourcesByBrand],
  );

  const handleExportReferences = async () => {
    setIsExporting(true);
    try {
      const allRefs: Record<string, unknown[]> = {};
      for (const brand of selectedBrandsList) {
        const refs = await getReferencesForBrand(brand.id, 10000);
        allRefs[brand.name] = refs;
      }
      const json = JSON.stringify(allRefs, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `references-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported references for ${selectedBrandsList.length} brand(s)`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Export the currently-filtered brand rows as CSV (respects search + all active filters)
  const handleDownloadCsv = async () => {
    const escapeCsv = (value: string) =>
      /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

    // Every column but Year Ceased is carried by the Algolia row. That one is
    // only meaningful for a ceased status, so the export reads the documents for
    // those rows alone — around a dozen brands, not the whole filtered set.
    let exportRows = processedBrands;
    const ceasedIds = processedBrands
      .filter(
        (b) =>
          b.status !== undefined &&
          CEASED_BRAND_STATUSES.includes(b.status) &&
          !hydratedIds.has(b.id),
      )
      .map((b) => b.id);
    if (!loadedFromFirestore && ceasedIds.length > 0) {
      try {
        const docs = await getWatchBrandsByIds(ceasedIds);
        exportRows = mergeBrandDocs(processedBrands, docs);
        setBrands((prev) => mergeBrandDocs(prev, docs));
        setHydratedIds((prev) => {
          const next = new Set(prev);
          docs.forEach((d) => next.add(d.id));
          return next;
        });
      } catch (error) {
        console.warn('Year Ceased unavailable for export', error);
      }
    }

    const headers = ['Brand Name', 'Status', 'Country', 'Year Founded', 'Year Ceased', 'Parent Org'];
    const rows = exportRows.map((brand) => [
      brand.displayName || brand.name || '',
      brand.status || 'active',
      brand.country || '',
      brand.founded != null ? String(brand.founded) : '',
      brand.yearCeased != null ? String(brand.yearCeased) : '',
      brand.parentOrg || '',
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => escapeCsv(cell)).join(','))
      .join('\r\n');

    // Prepend a UTF-8 BOM so Excel decodes accented characters correctly
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brands-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${processedBrands.length} brand(s)`);
  };

  const [bulkMbwdbChecking, setBulkMbwdbChecking] = useState(false);

  /**
   * Check MBWDB for a brand. Returns 'microbrand' | 'not_microbrand' | 'not_found'.
   * MBWDB categories: "Microbrand", "Microbrand (Legacy)", "Independent Brand" → microbrand
   *                   "Non-Microbrand" → not microbrand
   */
  const checkMbwdb = async (brandName: string): Promise<'microbrand' | 'not_microbrand' | 'not_found'> => {
    if (!brandName.trim()) return 'not_found';
    const html = await fetchViaCloudflareProxy(`https://mbwdb.com/?s=${encodeURIComponent(brandName.trim())}`);
    if (!html || !html.includes('entry-title')) return 'not_found';

    // Parse each search result: check if name matches AND what category it's in
    // Results are structured as articles with entry-title (name) and entry-categories (category)
    // We need to find the article whose title matches our brand and check its category
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const articles = doc.querySelectorAll('article');

    for (const article of articles) {
      const titleEl = article.querySelector('.entry-title a');
      if (!titleEl) continue;
      const title = titleEl.textContent?.trim().toLowerCase();
      if (title !== brandName.trim().toLowerCase()) continue;

      // Found matching brand — check its category
      const categoryLinks = article.querySelectorAll('.entry-categories a');
      const categories = Array.from(categoryLinks).map(a => a.textContent?.trim().toLowerCase() || '');

      if (categories.some(c => c === 'non-microbrand')) {
        return 'not_microbrand';
      }
      if (categories.some(c =>
        c === 'microbrand' || c.includes('microbrand') || c === 'independent brand'
      )) {
        return 'microbrand';
      }
      // Found brand but couldn't determine category — default to not_microbrand
      return 'not_microbrand';
    }

    return 'not_found';
  };

  const [mbwdbProgress, setMbwdbProgress] = useState('');

  const handleBulkMbwdbCheck = async () => {
    const selected = brands.filter(b => selectedBrands.has(b.id));
    if (selected.length === 0) return;
    setBulkMbwdbChecking(true);
    let found = 0;
    let notFound = 0;
    let errors = 0;
    // The MB flags this run wrote, applied to the loaded rows at the end. The
    // page used to reload the whole collection twice over to pick them up, which
    // now means re-reading Algolia — where the write has not landed yet, so the
    // reload would show the pre-check flags.
    const applied: Record<string, boolean> = {};

    for (let i = 0; i < selected.length; i++) {
      const brand = selected[i];
      const brandName = brand.name?.trim();
      setMbwdbProgress(`${i + 1}/${selected.length}: ${brandName || '?'}`);

      if (!brandName) { notFound++; continue; }

      // Try with retry on failure (MBWDB rate-limits aggressively)
      let result: 'microbrand' | 'not_microbrand' | 'not_found' = 'not_found';
      try {
        result = await checkMbwdb(brandName);
      } catch {
        // First failure — wait 30s and retry once
        setMbwdbProgress(`${i + 1}/${selected.length}: ${brandName} (rate limited, waiting 30s...)`);
        await new Promise(r => setTimeout(r, 30000));
        try {
          result = await checkMbwdb(brandName);
        } catch {
          errors++;
          continue;
        }
      }

      if (result === 'microbrand') {
        await updateWatchBrand(brand.id, { isMicroBrand: true });
        applied[brand.id] = true;
        found++;
      } else if (result === 'not_microbrand') {
        await updateWatchBrand(brand.id, { isMicroBrand: false });
        applied[brand.id] = false;
        notFound++;
      } else {
        notFound++;
      }

      // 5 second delay between requests to avoid rate limiting
      if (i < selected.length - 1) {
        await new Promise(r => setTimeout(r, 5000));
      }
    }

    setMbwdbProgress('');
    setBrands((prev) =>
      prev.map((b) =>
        b.id in applied ? { ...b, isMicroBrand: applied[b.id] } : b,
      ),
    );
    toast.success(`MBWDB check: ${found} microbrands found, ${notFound} not found${errors ? `, ${errors} errors` : ''}`);
    setBulkMbwdbChecking(false);
  };

  const handleBulkShopifyImport = async () => {
    if (selectedShopifyBrands.length === 0) return;
    onSelectBrand(await ensureFullBrand(selectedShopifyBrands[0]));
    if (selectedShopifyBrands.length > 1) {
      toast(`Navigated to ${selectedShopifyBrands[0].name}. Run imports one brand at a time.`);
    }
    clearSelection();
  };

  // Clicking a column header is a manual pick: it goes through `pickSort`, which
  // is what stops a later query auto-switching back to relevance.
  const toggleSort = (field: BrandSortField) => {
    // One `updateListState` per click: it spreads a closed-over `listState`, so
    // a second call in the same handler would overwrite the first. The manual
    // pick is recorded separately for exactly that reason.
    markUserPicked();
    if (effectiveSortField === field && !relevanceActive) {
      updateListState({
        sortField: field,
        sortDir: sortDir === 'asc' ? 'desc' : 'asc',
        currentPage: 1,
      });
    } else {
      updateListState({
        sortField: field,
        sortDir: field === 'name' || field === 'country' ? 'asc' : 'desc',
        currentPage: 1,
      });
    }
  };

  // Highlights the EFFECTIVE field: while relevance is on, no column owns the
  // order, and pointing an arrow at Name would be a lie.
  const SortIcon = ({ field }: { field: BrandSortField }) => {
    const active = !relevanceActive && effectiveSortField === field;
    return (
      <span className={`ml-1 inline-block ${active ? 'text-brand-600' : 'text-gray-300'}`}>
        {active && sortDir === 'desc' ? '▼' : '▲'}
      </span>
    );
  };

  /**
   * Open the edit form on the brand's FULL document, never on a listing row.
   *
   * The form is saved as a whole, so opening it on an Algolia projection would
   * write blanks over description, information, logo, hero image, glyph and the
   * rest — fields the index doesn't carry and the form would therefore believe
   * are empty.
   */
  const handleEdit = async (row: WatchBrand) => {
    const brand = await ensureFullBrand(row);
    // Opening a brand by hand is never a seeded edit, whatever was open before.
    setActiveEditSeed(null);
    populateEditForm(brand);
  };

  /** Load a FULL brand document into the edit form and open the modal. */
  const populateEditForm = (brand: WatchBrand) => {
    setEditingBrand(brand);
    loadFormFields(brand, 'edit');
  };

  /**
   * Every form field from `brand` — a stored document in edit mode, the seed's
   * values (or nothing) in create mode.
   */
  const loadFormFields = (brand: Partial<WatchBrand>, mode: 'create' | 'edit') => {
    // A hint belongs to the brand it was written for.
    setEnrichHint('');
    setEditName(brand.name || '');
    setEditDisplayName(brand.displayName || '');
    const descriptionSeed = richDescriptionSeed(brand);
    setEditDescription(descriptionSeed);
    setEditDescriptionSeed(descriptionSeed);
    setEditCountry(brand.country || '');
    setEditLocation(brandLocationFormFromBrand(brand));
    setEditCommerce(brandCommerceFormFromBrand(brand));
    setEditIdentity(brandIdentityFormFromBrand(brand));
    setEditWebsite(brand.mainWebsite || '');
    setEditAddress(brand.mainAddress || '');
    setEditHeroImage(brand.heroImage || '');
    setEditHeroImageSourceUrl(brand.heroImageSourceUrl || '');
    setEditUseHeroImage(brand.useHeroImage || false);
    setEditLogoHeroTransparent(brand.logoHeroTransparent ?? false);
    setEditLogo(brand.logo || '');
    setEditLogoSourceUrl(brand.logoSourceUrl || '');
    setEditIsMicroBrand(mode === 'edit' ? brand.isMicroBrand || false : brand.isMicroBrand);
    setEditDedupIdentityField(brand.dedupIdentityField === 'model' ? 'model' : 'reference');
    setEditIsPromoted(brand.isPromoted || false);
    setEditPromotedOrder(brand.promotedOrder || 0);
    setEditIsDisabled(brand.isDisabled || false);
    setEditStatus(brand.status || 'active');
    setEditNeedsReview(brand.needsReview || false);
    setEditHideImages(brand.hideImages || false);
    // Opt-in: a brand with no value is OFF, as it is in the app.
    setEditShowProductLinks(brandShowsProductLinks(brand.showProductLinks));
    setMbwdbResult(null);
    setEditInfo(brand.information || '');
    setEditAltNames(brand.alternativeNames || []);
    setEditAbbreviations(brand.abbreviations || []);
    setEditWindingDirections(brand.windingDirections || []);
    setEditFounded(brand.founded?.toString() || '');
    setEditParentOrg(brand.parentOrg || '');
    setEditParentOrgId(brand.parentOrgId || '');
    // Acquisitions: prefer the new structured array. When absent but the legacy
    // one-dimensional `heritageBrandIds` is set (PR #153), auto-migrate each id
    // into an acquisition record on load. The legacy field is removed from the
    // doc on save (see handleSave). Mapping defaults: legacy heritage links were
    // defunct-only trademark revivals, so rights_acquisition_for_revival /
    // successor_brand_lineage is the safest provenance-preserving default in the
    // new vocabulary — admin can refine per-record in the editor. See report.
    const legacyHeritageIds =
      (brand as { heritageBrandIds?: string[] }).heritageBrandIds ?? [];
    setEditAcquisitions(
      brand.acquisitions && brand.acquisitions.length > 0
        ? brand.acquisitions
        : legacyHeritageIds.map((id) => ({
            acquiredBrandId: id,
            acquisitionType: 'rights_acquisition_for_revival' as const,
            directness: 'successor_brand_lineage' as const,
          })),
    );
    setEditYearCeased(brand.yearCeased);
    setEditDocs(brand.docs ?? []);
    setEditAiDescription(brand.aiDescription || '');
    setEditGlyphDescription(brand.glyphDescription || '');
    setEditGlyphImageUrl(brand.glyphImageUrl || '');
  };

  // Allow parent to programmatically open the edit modal
  useEffect(() => {
    if (editBrandRequest) {
      void handleEdit(editBrandRequest);
    }
  }, [editBrandRequest]);

  // External import, Update path: open the Edit modal on the matched brand and
  // lay the reviewer's ticked values over the stored ones. The brand is read
  // fresh rather than taken from the listing — the listing row may be an
  // Algolia projection, and the overlay must sit on the real document.
  useEffect(() => {
    if (!editBrandPatchSeed) return;
    const seed = editBrandPatchSeed;
    let cancelled = false;
    (async () => {
      try {
        const brand = await getWatchBrandById(seed.brandId);
        if (cancelled) return;
        if (!brand) {
          toast.error(`Brand "${seed.brandId}" no longer exists`);
          return;
        }
        populateEditForm(brand);
        const v = seed.values;
        if (v.name !== undefined) setEditName(v.name);
        if (v.displayName !== undefined) setEditDisplayName(v.displayName);
        // A verified country is locked against imports. The reviewer grid
        // already refuses to include it; this holds the line for any seed that
        // carries one anyway.
        if (v.country !== undefined && brand.countryVerified !== true) setEditCountry(v.country);
        const seededLocation = Object.fromEntries(
          LOCATION_TEXT_KEYS.filter((key) => v[key] !== undefined).map((key) => [key, v[key]]),
        );
        if (Object.keys(seededLocation).length > 0) {
          setEditLocation({ ...brandLocationFormFromBrand(brand), ...seededLocation });
        }
        setEditCommerce(applySeedToCommerceForm(brandCommerceFormFromBrand(brand), v));
        if (v.mainWebsite !== undefined) setEditWebsite(v.mainWebsite);
        if (v.mainAddress !== undefined) setEditAddress(v.mainAddress);
        if (v.founded !== undefined) setEditFounded(String(v.founded));
        if (v.logo !== undefined) {
          setEditLogo(v.logo);
          setEditLogoSourceUrl(v.logo);
        }
        if (v.heroImage !== undefined) {
          setEditHeroImage(v.heroImage);
          setEditHeroImageSourceUrl(v.heroImage);
        }
        if (v.isMicroBrand !== undefined) setEditIsMicroBrand(v.isMicroBrand);
        setActiveEditSeed(seed);
      } catch (error) {
        console.error('Failed to open seeded brand edit:', error);
        if (!cancelled) toast.error('Could not open the brand for update');
      } finally {
        if (!cancelled) onEditBrandPatchSeedConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBrandPatchSeed]);

  // Notify parent when edit modal closes
  const handleCloseEdit = () => {
    const cancelledSeed = activeEditSeed;
    setEditingBrand(null);
    setActiveEditSeed(null);
    onEditBrandComplete?.();
    if (cancelledSeed) onBrandSeedCancelled?.(cancelledSeed);
  };

  /** Open the form in create mode — blank, or with an external-import seed's values. */
  const openCreate = (seed: CreateBrandSeed | null = null) => {
    setEditingBrand(null);
    setActiveEditSeed(null);
    const v = seed?.values ?? {};
    // Fields the seed does not carry are blanked, so nothing typed into an
    // earlier, abandoned create bleeds in.
    loadFormFields(
      {
        name: v.name,
        displayName: v.displayName,
        country: v.country,
        mainWebsite: v.mainWebsite,
        mainAddress: v.mainAddress,
        founded: v.founded,
        // A seeded flag shows in the box — and unticking it there means it is not written.
        isMicroBrand: v.isMicroBrand,
      },
      'create',
    );
    setEditLocation({
      ...emptyBrandLocationForm,
      city: v.city ?? '',
      stateRegion: v.stateRegion ?? '',
      assemblyLocation: v.assemblyLocation ?? '',
      designLocation: v.designLocation ?? '',
    });
    setEditCommerce(applySeedToCommerceForm(emptyBrandCommerceForm, v));
    setActiveCreateSeed(seed);
    setIsCreating(true);
  };

  const closeCreate = () => {
    setActiveCreateSeed(null);
    setIsCreating(false);
  };

  /** Cancel / close in create mode — never the post-save hand-over to edit mode. */
  const handleCancelCreate = () => {
    const cancelledSeed = activeCreateSeed;
    closeCreate();
    if (cancelledSeed) onBrandSeedCancelled?.(cancelledSeed);
  };

  // External import, Create path: open the form in create mode with the
  // reviewer's ticked values in the inputs.
  useEffect(() => {
    if (!createBrandSeed) return;
    openCreate(createBrandSeed);
    onCreateBrandSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createBrandSeed]);

  const handleCreate = async () => {
    if (!editName.trim()) {
      toast.error('Brand name is required');
      return;
    }

    setIsProcessing(true);
    try {
      const foundedNum = editFounded.trim() ? parseInt(editFounded.trim(), 10) : null;
      // A new brand only gets what was filled in: an empty input, an unticked
      // box or a default select writes nothing (status 'active', dedup
      // 'reference' are the defaults the app assumes when the field is absent).
      const ceased = editStatus === 'defunct' || editStatus === 'dormant' || editStatus === 'absorbed';
      const docs = cleanDocsLinks(editDocs);
      const acquisitions = editAcquisitions
        .filter((a) => a.acquiredBrandId?.trim() || a.formerName?.trim())
        .map(
          (a) =>
            Object.fromEntries(
              Object.entries(a).filter(([, v]) => v !== undefined && v !== ''),
            ) as unknown as BrandAcquisition,
        );
      const brandData = {
        name: editName.trim(),
        displayName: editDisplayName.trim() || editName.trim(),
        // Dual storage: the rich HTML, and its plain text for older app builds.
        ...(isEmptyRichText(editDescription)
          ? {}
          : { descriptionRich: editDescription, description: htmlToPlainText(editDescription) }),
        alternativeNames: editAltNames.length > 0 ? editAltNames : [],
        abbreviations: editAbbreviations.length > 0 ? editAbbreviations : [],
        windingDirections: editWindingDirections.length > 0 ? editWindingDirections : [],
        mainWebsite: editWebsite.trim() || undefined,
        mainAddress: editAddress.trim() || undefined,
        information: editInfo.trim() || undefined,
        country: editCountry.trim() || undefined,
        ...brandLocationCreatePayload(editLocation),
        // isShopifyBrand, ecommercePlatform, instagramUrl, founder.
        ...brandCommerceCreatePayload(editCommerce),
        // ownershipType, and yearRevived / predecessorBrand for a revived brand.
        ...brandIdentityCreatePayload(editIdentity, editStatus),
        status: editStatus === 'active' ? undefined : editStatus,
        isMicroBrand: editIsMicroBrand,
        founded: foundedNum && !isNaN(foundedNum) ? foundedNum : undefined,
        yearCeased: ceased ? editYearCeased : undefined,
        parentOrg: editParentOrg.trim() || undefined,
        parentOrgId: editParentOrgId.trim() || undefined,
        aiDescription: editAiDescription.trim() || undefined,
        glyphDescription: editGlyphDescription.trim() || undefined,
        acquisitions: acquisitions.length > 0 ? acquisitions : undefined,
        docs: docs.length > 0 ? docs : undefined,
        isPromoted: editIsPromoted || undefined,
        promotedOrder: editIsPromoted ? editPromotedOrder : undefined,
        isDisabled: editIsDisabled || undefined,
        needsReview: editNeedsReview || undefined,
        hideImages: editHideImages || undefined,
        logoHeroTransparent: editLogoHeroTransparent || undefined,
        dedupIdentityField: editDedupIdentityField === 'model' ? ('model' as const) : undefined,
        // New brands are opt-in for the explore "View product page" link
        // (potentially a paid feature). Written as an explicit boolean — `false`
        // unless ticked — so the doc says what the app will do.
        showProductLinks: editShowProductLinks,
      };
      // Remove undefined values before sending to Firestore
      const cleanBrandData = Object.fromEntries(
        Object.entries(brandData).filter(([, v]) => v !== undefined)
      ) as typeof brandData;

      // Seeded from an external catalogue: add the fields this modal has no
      // input for, plus the provenance stamps. The logo is held back — it has
      // to be pulled into our Storage, which needs the new brand's id.
      const seed = activeCreateSeed;
      const { logo: seededLogo, ...seedPayload } = seed
        ? buildSeedWritePayload({ seed, formKeys: CREATE_FORM_KEYS, saved: cleanBrandData })
        : ({} as Partial<WatchBrand>);
      const createPayload = {
        ...cleanBrandData,
        ...seedPayload,
        ...(seedPayload.heroImage ? { heroImageSourceUrl: seedPayload.heroImage } : {}),
      };

      const brandId = await createWatchBrand(createPayload);

      // The brand exists from here on, so a logo problem must not surface as
      // "Failed to create brand" — it is reported on its own and the create
      // still completes.
      let logoFields: Partial<WatchBrand> = {};
      if (seededLogo) {
        try {
          let resolved: Partial<WatchBrand>;
          try {
            resolved = await resolveLogoToStorage({
              entity: 'brands',
              id: brandId,
              input: seededLogo,
            });
          } catch (err) {
            if (!(err instanceof LogoFetchError)) throw err;
            resolved = { logoSourceUrl: err.logoSourceUrl };
            toast.error('Could not fetch the logo image from its URL — saved the source URL only.');
          }
          await updateWatchBrand(brandId, resolved);
          logoFields = resolved;
        } catch (err) {
          console.error('Brand created, but saving its logo failed:', err);
          toast.error('Brand created, but its logo could not be saved — add it from Edit.');
        }
      }

      // A seeded hero arrives as the source site's URL; it gets our own copy
      // now that the brand has an id. A failure keeps the URL already written.
      let heroFields: Partial<WatchBrand> = {};
      if (createPayload.heroImage) {
        try {
          const hero = await resolveHeroToStorage({
            entity: 'brands',
            id: brandId,
            input: createPayload.heroImage,
            manualSourceUrl: createPayload.heroImageSourceUrl,
          });
          if (hero.fetchError) {
            toast.error(`Brand created, but its hero image could not be copied into Storage (${hero.fetchError}) — kept its URL.`);
          } else if (hero.heroImage !== createPayload.heroImage) {
            heroFields = { heroImage: hero.heroImage, heroImageSourceUrl: hero.heroImageSourceUrl };
            await updateWatchBrand(brandId, heroFields);
          }
        } catch (err) {
          console.error('Brand created, but saving its hero image failed:', err);
          toast.error('Brand created, but its hero image could not be saved — add it from Edit.');
          heroFields = {};
        }
      }

      const newBrandObj: WatchBrand = {
        id: brandId,
        ...createPayload,
        ...logoFields,
        ...heroFields,
      } as WatchBrand;

      setBrands((prev) =>
        [...prev, newBrandObj].sort((a, b) => a.name.localeCompare(b.name)),
      );
      // The form built this record in full, so it needs no hydration read — and
      // must not get one: Algolia has not indexed the new brand yet, and a read
      // that raced the create would be the only way this row could regress.
      setHydratedIds((prev) => new Set(prev).add(brandId));
      requestedHydration.current.add(brandId);
      // The form carries on in edit mode on the saved brand, so images and the
      // timeline — which need its id — can be added without reopening it.
      toast.success('Brand created — images and timeline can be added now');
      closeCreate();
      populateEditForm(newBrandObj);
      if (seed) onCreateBrandSeedApplied?.(brandId, seed);
    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to create brand',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!editingBrand) return;

    if (!editName.trim()) {
      toast.error('Brand name is required');
      return;
    }

    // For optional text fields: persist the trimmed value; emit '' to clear a
    // previously-set value so blanking actually persists; undefined (→ stripped)
    // only when the field was never set, so untouched brands aren't polluted.
    const clearable = (next: string, prev: string | undefined) =>
      next.trim() || (prev ? '' : undefined);

    setIsProcessing(true);
    try {
      // Pull any external logo URL into our own Storage (mirrors the hero flow),
      // writing the canonical Storage URL to `logo` + provenance to `logoSourceUrl`.
      let resolvedLogo: { logo: string; logoSourceUrl: string };
      try {
        resolvedLogo = await resolveLogoToStorage({
          entity: 'brands',
          id: editingBrand.id,
          input: editLogo,
          manualSourceUrl: editLogoSourceUrl,
          prev: { logo: editingBrand.logo, logoSourceUrl: editingBrand.logoSourceUrl },
        });
      } catch (err) {
        if (err instanceof LogoFetchError) {
          resolvedLogo = { logo: '', logoSourceUrl: err.logoSourceUrl };
          toast.error('Could not fetch the logo image from its URL — saved the source URL only.');
        } else {
          throw err;
        }
      }

      // A pasted hero URL becomes our own Storage copy, as the logo does. A
      // fetch that fails keeps the URL the brand had and says so.
      const resolvedHero = await resolveHeroToStorage({
        entity: 'brands',
        id: editingBrand.id,
        input: editHeroImage,
        manualSourceUrl: editHeroImageSourceUrl,
        prev: { heroImage: editingBrand.heroImage, heroImageSourceUrl: editingBrand.heroImageSourceUrl },
      });
      if (resolvedHero.fetchError) {
        toast.error(`Could not copy the hero image into Storage (${resolvedHero.fetchError}) — kept its URL.`);
      }

      const foundedNum = editFounded.trim() ? parseInt(editFounded.trim(), 10) : null;
      // isShopifyBrand, ecommercePlatform, instagramUrl, founder — like the
      // create path, the edit payload used to leave these out entirely.
      const commerce = brandCommerceUpdatePayload(editCommerce, editingBrand);
      // ownershipType, yearRevived, predecessorBrand. The two revival fields
      // follow the status: off `revived`, a stored one is deleted.
      const identity = brandIdentityUpdatePayload(editIdentity, editStatus, editingBrand);
      const updateData: Partial<WatchBrand> = {
        name: editName.trim(),
        displayName: editDisplayName.trim() || editName.trim(),
        // Only when the rich editor changed: HTML + its plain text, together.
        ...(editDescription !== editDescriptionSeed ? richDescriptionPatch(editDescription, editingBrand) : {}),
        country: clearable(editCountry, editingBrand.country),
        ...brandLocationUpdatePayload(editLocation, editingBrand),
        ...commerce.payload,
        ...identity.payload,
        alternativeNames: editAltNames.length > 0 ? editAltNames : [],
        abbreviations: editAbbreviations.length > 0 ? editAbbreviations : [],
        windingDirections: editWindingDirections.length > 0 ? editWindingDirections : [],
        mainWebsite: clearable(editWebsite, editingBrand.mainWebsite),
        mainAddress: clearable(editAddress, editingBrand.mainAddress),
        heroImage: clearable(resolvedHero.heroImage, editingBrand.heroImage),
        heroImageSourceUrl: clearable(resolvedHero.heroImageSourceUrl, editingBrand.heroImageSourceUrl),
        // Emit '' only to clear a previously-set value; undefined (→ stripped)
        // when there was never a logo, so untouched brands aren't polluted.
        logo: resolvedLogo.logo || (editingBrand.logo ? '' : undefined),
        logoSourceUrl:
          resolvedLogo.logoSourceUrl || (editingBrand.logoSourceUrl ? '' : undefined),
        useHeroImage: editUseHeroImage,
        logoHeroTransparent: editLogoHeroTransparent,
        isMicroBrand: editIsMicroBrand,
        isPromoted: editIsPromoted,
        promotedOrder: editIsPromoted ? editPromotedOrder : undefined,
        isDisabled: editIsDisabled,
        hideImages: editHideImages,
        showProductLinks: editShowProductLinks,
        information: clearable(editInfo, editingBrand.information),
        founded: foundedNum && !isNaN(foundedNum) ? foundedNum : undefined,
        parentOrg: clearable(editParentOrg, editingBrand.parentOrg),
        aiDescription: clearable(editAiDescription, editingBrand.aiDescription),
        glyphDescription: clearable(
          editGlyphDescription,
          editingBrand.glyphDescription,
        ),
        glyphImageUrl: clearable(editGlyphImageUrl, editingBrand.glyphImageUrl),
      };
      // Remove undefined values before sending to Firestore
      const cleanData = Object.fromEntries(
        Object.entries(updateData).filter(([, v]) => v !== undefined)
      ) as Partial<WatchBrand>;
      // Status: write the marker, or delete the stored field when reverting to
      // the default `active` (so an old `defunct`/`dormant` value is removed
      // rather than left stale). active stays absent => implicitly active.
      const nextStatus: BrandStatus | undefined =
        editStatus === 'active' ? undefined : editStatus;
      // Acquisitions: drop incomplete cards (no acquired brand picked), then
      // strip per-record undefined optionals so Firestore doesn't reject them.
      // Write the array when non-empty; delete a stored field when cleared back
      // to empty; omit entirely when there was never one.
      const cleanedAcquisitions: BrandAcquisition[] = editAcquisitions
        // Keep a card if it links a brand (regular types) OR records a former
        // name (name-change types). Drops incomplete cards either way.
        .filter((a) => a.acquiredBrandId?.trim() || a.formerName?.trim())
        .map((a) =>
          Object.fromEntries(
            Object.entries(a).filter(([, v]) => v !== undefined && v !== ''),
          ) as unknown as BrandAcquisition,
        );
      const nextAcquisitions =
        cleanedAcquisitions.length > 0 ? cleanedAcquisitions : undefined;
      // Legacy heritageBrandIds is eliminated by the new acquisitions model:
      // delete it from the doc whenever it was present (the migration on load
      // already folded it into acquisitions).
      const hadLegacyHeritage = Boolean(
        (editingBrand as { heritageBrandIds?: string[] }).heritageBrandIds
          ?.length,
      );
      // Year ceased: only meaningful for ceased brands (defunct | dormant |
      // absorbed). Write the number when the brand is in one of those states and
      // a year was entered; otherwise delete a stored field (covers cleared input
      // AND status changing off a ceased state — e.g. to active/revived). Mirrors
      // the status/heritage convention. Validation is advisory, never blocks save.
      const yearCeasedApplies =
        editStatus === 'defunct' ||
        editStatus === 'dormant' ||
        editStatus === 'absorbed';
      const nextYearCeased =
        yearCeasedApplies && editYearCeased !== undefined
          ? editYearCeased
          : undefined;
      // Needs-review audit flag: write `true` when set, delete the stored field
      // when cleared (so it doesn't linger false). Absent ⇒ not flagged.
      const nextNeedsReview = editNeedsReview ? true : undefined;
      // Docs links: drop abandoned blank rows, then follow the acquisitions
      // convention — a non-empty array writes, an emptied one deletes the stored
      // field. `undefined` alone can't clear it: updateWatchBrand runs the
      // updates through stripUndefined, which would drop the key entirely and
      // leave the old links in place.
      const cleanedDocs = cleanDocsLinks(editDocs);
      const nextDocs = cleanedDocs.length > 0 ? cleanedDocs : undefined;
      // The FK follows the docs/status convention: write it when set, delete a
      // stored one when cleared. An empty string would be a foreign key to
      // nothing, and undefined alone is stripped, leaving the old link in place.
      const nextParentOrgId = editParentOrgId.trim();
      // Seeded from an external catalogue: the fields this modal has no input
      // for, plus the provenance stamps, merged over what the brand holds.
      const seed = activeEditSeed?.brandId === editingBrand.id ? activeEditSeed : null;
      const seedPayload = seed
        ? buildSeedWritePayload({
            seed,
            formKeys: EDIT_FORM_KEYS,
            saved: cleanData,
            existing: editingBrand,
          })
        : {};
      await updateWatchBrand(editingBrand.id, {
        ...cleanData,
        ...seedPayload,
        ...(nextParentOrgId
          ? { parentOrgId: nextParentOrgId }
          : editingBrand.parentOrgId
          ? { parentOrgId: deleteField() as unknown as undefined }
          : {}),
        ...(nextStatus
          ? { status: nextStatus }
          : editingBrand.status
          ? { status: deleteField() as unknown as undefined }
          : {}),
        ...(nextNeedsReview
          ? { needsReview: true }
          : editingBrand.needsReview
          ? { needsReview: deleteField() as unknown as undefined }
          : {}),
        ...(nextAcquisitions
          ? { acquisitions: nextAcquisitions }
          : editingBrand.acquisitions?.length
          ? { acquisitions: deleteField() as unknown as undefined }
          : {}),
        ...(nextDocs
          ? { docs: nextDocs }
          : editingBrand.docs?.length
          ? { docs: deleteField() as unknown as undefined }
          : {}),
        // Always remove the legacy field once it existed (one-way migration).
        ...(hadLegacyHeritage
          ? { heritageBrandIds: deleteField() as unknown as undefined }
          : {}),
        ...(nextYearCeased !== undefined
          ? { yearCeased: nextYearCeased }
          : editingBrand.yearCeased !== undefined
          ? { yearCeased: deleteField() as unknown as undefined }
          : {}),
        // Dedup identity field: persist 'model' when chosen; delete the stored
        // field when reverting to the default 'reference' (so the doc stays clean
        // and falls back to default behavior); omit when there was never one.
        ...(editDedupIdentityField === 'model'
          ? { dedupIdentityField: 'model' as const }
          : editingBrand.dedupIdentityField
          ? { dedupIdentityField: deleteField() as unknown as undefined }
          : {}),
        // An emptied founder is deleted, not written as an empty map.
        ...(commerce.clearFounder
          ? { founder: deleteField() as unknown as undefined }
          : {}),
        // Identity fields the form no longer describes.
        ...Object.fromEntries(
          identity.clear.map((key) => [key, deleteField() as unknown as undefined]),
        ),
      });

      const updatedBrand: WatchBrand = {
        ...editingBrand,
        ...cleanData,
        ...seedPayload,
        status: nextStatus,
        needsReview: nextNeedsReview,
        acquisitions: nextAcquisitions,
        docs: nextDocs,
        yearCeased: nextYearCeased,
        dedupIdentityField:
          editDedupIdentityField === 'model' ? 'model' : undefined,
        ...(commerce.clearFounder ? { founder: undefined } : {}),
        ...Object.fromEntries(identity.clear.map((key) => [key, undefined])),
      };
      // Drop the legacy field from the in-memory copy too (it was deleted in Firestore).
      delete (updatedBrand as { heritageBrandIds?: string[] }).heritageBrandIds;

      setBrands((prev) =>
        prev.map((b) => (b.id === editingBrand.id ? updatedBrand : b)),
      );
      // The docs chips elsewhere in the admin read through a module-level
      // cache, so an edited link would otherwise stay stale for the session.
      invalidateDocsLinks('brand', editingBrand.id);

      toast.success('Brand updated successfully');
      setEditingBrand(null);
      setActiveEditSeed(null);
      onEditBrandComplete?.(updatedBrand);
      if (seed) onEditBrandPatchSeedApplied?.(editingBrand.id, seed);
    } catch (error) {
      console.error('Error updating brand:', error);
      toast.error('Failed to update brand');
    } finally {
      setIsProcessing(false);
    }
  };

  // AI field handlers (create and edit) - map suggestion keys to form field setters
  const aiFieldHandlers: Record<
    string,
    { label: string; currentDisplay: string; apply: (v: unknown) => void; formatSuggested?: (v: unknown) => string }
  > = {
    aiDescription: {
      label: 'AI Description',
      currentDisplay: editAiDescription,
      apply: (v) => typeof v === 'string' && setEditAiDescription(v),
    },
    // The rich (HTML) description the model returns beside aiDescription —
    // already allowlist-cleaned server-side. Lands in the Description editor,
    // whose save writes descriptionRich + its plain text to description.
    descriptionRich: {
      label: 'Description (rich)',
      currentDisplay: htmlToPlainText(editDescription),
      apply: (v) => typeof v === 'string' && setEditDescription(v),
      formatSuggested: (v) => (typeof v === 'string' ? htmlToPlainText(v) : ''),
    },
    founded: {
      label: 'Founded Year',
      currentDisplay: editFounded,
      apply: (v) => {
        if (typeof v === 'number') setEditFounded(v.toString());
        else if (typeof v === 'string') setEditFounded(v);
      },
    },
    country: {
      label: 'Country',
      currentDisplay: editCountry,
      apply: (v) => typeof v === 'string' && setEditCountry(v),
    },
    mainAddress: {
      label: 'Address',
      currentDisplay: editAddress,
      apply: (v) => typeof v === 'string' && setEditAddress(v),
    },
    mainWebsite: {
      label: 'Website',
      currentDisplay: editWebsite,
      apply: (v) => typeof v === 'string' && setEditWebsite(v),
    },
    parentOrg: {
      label: 'Parent Organization',
      currentDisplay: editParentOrg,
      apply: (v) => {
        if (typeof v !== 'string') return;
        const next = parentOrgFromText(v, parentOrgIndex);
        setEditParentOrg(next.parentOrg);
        setEditParentOrgId(next.parentOrgId);
      },
    },
    glyphDescription: {
      label: 'Glyph Description',
      currentDisplay: editGlyphDescription,
      apply: (v) => typeof v === 'string' && setEditGlyphDescription(v),
    },
    // city, stateRegion, assemblyLocation, designLocation, instagramUrl,
    // ecommercePlatform, founder.name, isMicroBrand, ownershipType, status,
    // yearRevived, predecessorBrand.
    ...buildBrandAiHandlers({
      location: editLocation,
      setLocation: setEditLocation,
      commerce: editCommerce,
      setCommerce: setEditCommerce,
      identity: editIdentity,
      setIdentity: setEditIdentity,
      status: editStatus,
      setStatus: setEditStatus,
      isMicroBrand: editIsMicroBrand,
      setIsMicroBrand: setEditIsMicroBrand,
    }),
  };

  /**
   * Inline "Generate" next to the glyph field. Same callable as the modal-wide
   * enrich, narrowed with focusFields so the model isn't asked to re-propose
   * the whole brand record, and applied straight to the field rather than
   * through the suggestions table.
   */
  const handleGenerateGlyph = async () => {
    if (!editName.trim()) {
      toast.error('Enter a brand name before generating');
      return;
    }
    setGlyphAiLoading(true);

    try {
      const result = await enrichBrandWithAI(
        {
          name: editName,
          displayName: editDisplayName,
          country: editCountry,
          founded: editFounded ? parseInt(editFounded, 10) : undefined,
          mainWebsite: editWebsite,
          description: htmlToPlainText(editDescription),
          glyphDescription: editGlyphDescription,
        },
        undefined,
        ['glyphDescription'],
      );

      const suggestion = result.suggestions?.glyphDescription;
      if (typeof suggestion !== 'string' || !suggestion.trim()) {
        toast('No distinctive glyph identified for this brand');
        return;
      }
      setEditGlyphDescription(suggestion.trim());
      toast.success('Glyph description generated');
    } catch (error) {
      console.error('Glyph generation failed:', error);
      const message =
        error instanceof Error ? error.message : 'Glyph generation failed';
      toast.error(message);
    } finally {
      setGlyphAiLoading(false);
    }
  };

  const handleAIEnrich = async (hint?: string) => {
    if (!editName.trim()) {
      toast.error('Enter a brand name before enriching');
      return;
    }
    setAiLoading(true);

    try {
      const result = await enrichBrandWithAI(
        {
          name: editName,
          displayName: editDisplayName,
          country: editCountry,
          founded: editFounded ? parseInt(editFounded, 10) : undefined,
          mainWebsite: editWebsite,
          mainAddress: editAddress,
          description: htmlToPlainText(editDescription),
          // The HTML too: the model can only keep the in-app links it is shown.
          descriptionRich: isEmptyRichText(editDescription) ? undefined : editDescription,
          aiDescription: editAiDescription,
          information: editInfo,
          glyphDescription: editGlyphDescription,
          // Shopify hint is derived from the canonical import-sources config,
          // not the legacy shopifyEnabled flag. sourcesByBrand is already in
          // scope here (used for the Shopify filter/pill). A brand being
          // created has no import sources yet.
          shopifyEnabled: editingBrand ? brandHasShopifySource(editingBrand, sourcesByBrand) : undefined,
          ...brandEnrichHints({
            location: editLocation,
            commerce: editCommerce,
            identity: editIdentity,
            status: editStatus,
            // On a stored brand `false` is the default, not a finding — only a
            // ticked box is worth telling the model about. A new brand's flag
            // is undefined until stated, so a seeded `false` is passed on.
            isMicroBrand: editingBrand ? editIsMicroBrand || undefined : editIsMicroBrand,
            parentOrg: editParentOrg,
          }),
        },
        undefined,
        undefined,
        hint,
      );

      // A proposed description that drops any in-app link of the current one is
      // never offered — checked here as well as server-side (descriptionLinkGuard).
      const guarded = withholdLinkLosingDescriptions(result, editDescription, {
        html: ['descriptionRich'],
        plain: ['description'],
      });
      const linkLoss = describeLinkLoss(guarded.losses);
      if (linkLoss) toast.error(linkLoss, { duration: 12000 });
      const suggestions = { ...guarded.result.suggestions };
      const confidence = { ...guarded.result.confidence };
      // A verified country is locked. The model researches the brand afresh on
      // every run and will often have an opinion; on a brand whose country a
      // curator has checked, that opinion is not offered at all.
      if (editLocation.countryVerified) {
        delete suggestions.country;
        delete confidence.country;
      }

      if (Object.keys(suggestions).length === 0) {
        toast('No new suggestions — fields look complete');
        return;
      }

      // Default-accept high-confidence suggestions
      const accepted: Record<string, boolean> = {};
      for (const key of Object.keys(suggestions)) {
        accepted[key] = confidence[key] === 'high';
      }

      setAiSuggestions(suggestions);
      setAiConfidence(confidence);
      setAiAccepted(accepted);
      setShowAISuggestions(true);
    } catch (error) {
      console.error('AI enrichment failed:', error);
      const message = error instanceof Error ? error.message : 'AI enrichment failed';
      toast.error(message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAISuggestions = () => {
    let applied = 0;
    for (const [key, value] of Object.entries(aiSuggestions)) {
      if (!aiAccepted[key]) continue;
      const handler = aiFieldHandlers[key];
      if (!handler) continue;
      handler.apply(value);
      applied++;
    }
    setShowAISuggestions(false);
    if (applied > 0) {
      toast.success(`Applied ${applied} AI suggestion${applied === 1 ? '' : 's'}`);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600'></div>
      </div>
    );
  }

  // Brands whose server-maintained counter has never been written. The totals
  // below simply exclude them, and say so — showing a spinner instead would
  // leave the pills stuck on "…" indefinitely, since nothing on this page can
  // fill the gap. `recomputeAllWatchBrandReferenceCounts` (watchlock) does.
  const uncountedBrands = brands.length - Object.keys(parentRefCounts).length;
  const uncountedNote =
    uncountedBrands > 0
      ? ` ${uncountedBrands} brand${uncountedBrands === 1 ? '' : 's'} not counted yet — run the brand reference-count recompute.`
      : '';

  return (
    <div className='flex flex-col gap-4'>
      {/* Stats bar */}
      <div className='flex items-center gap-3 flex-wrap'>
        <StatPill label='Total brands' value={brands.length} />
        <StatPill
          label='Total refs'
          value={totalRefs}
          title={`${REFS_TOOLTIP}${uncountedNote}`}
        />
        <StatPill label='Shopify enabled' value={shopifyCount} accent='emerald' />
        <StatPill
          label='With refs'
          value={brandsWithRefsCount}
          accent='blue'
          title={`Brands with at least one listable reference. ${REFS_TOOLTIP}${uncountedNote}`}
        />
      </div>

      {/* Geographic drill-down pills */}
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-2 flex-wrap'>
          {Object.entries(CONTINENT_MAP).map(([continent, { emoji }]) => {
            const count = continentCounts.byContinent[continent] ?? 0;
            if (count === 0) return null;
            const isActive = selectedContinent === continent;
            return (
              <button
                key={continent}
                type='button'
                onClick={() => {
                  if (isActive) {
                    updateListState({ selectedContinent: null, selectedCountry: null, currentPage: 1 });
                  } else {
                    updateListState({ selectedContinent: continent, selectedCountry: null, currentPage: 1 });
                  }
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {emoji} {continent} ({count})
              </button>
            );
          })}
          {continentCounts.unset > 0 && (
            <button
              type='button'
              onClick={() => {
                if (selectedContinent === 'Unset') {
                  updateListState({ selectedContinent: null, selectedCountry: null, currentPage: 1 });
                } else {
                  updateListState({ selectedContinent: 'Unset', selectedCountry: null, currentPage: 1 });
                }
              }}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedContinent === 'Unset'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              ❓ Unset ({continentCounts.unset})
            </button>
          )}
        </div>
        {selectedContinent && selectedContinent !== 'Unset' && (
          <div className='flex items-center gap-2 flex-wrap pl-2'>
            {(CONTINENT_MAP[selectedContinent]?.countries ?? [])
              .filter((country) => (countryCountsForContinent[country] ?? 0) > 0)
              .map((country) => {
                const count = countryCountsForContinent[country] ?? 0;
                const isActive = selectedCountry === country;
                const flag = COUNTRY_FLAGS[country.toLowerCase()] ?? '';
                return (
                  <button
                    key={country}
                    type='button'
                    onClick={() => {
                      if (isActive) {
                        updateListState({ selectedCountry: null, currentPage: 1 });
                      } else {
                        updateListState({ selectedCountry: country, currentPage: 1 });
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {flag} {country} ({count})
                  </button>
                );
              })}
          </div>
        )}
      </div>

      {/* Parent organization filter pills */}
      <div className='flex items-center gap-2 flex-wrap'>
        {parentOrgCounts.chips.map((chip) => {
            const isActive = selectedParentOrg === chip.key;
            // Values that resolve to no single organization keep their raw text
            // and go amber, so the chips double as a to-reconcile list.
            const inactiveClasses = chip.matched
              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200';
            return (
              <button
                key={chip.key}
                type='button'
                title={chip.title}
                onClick={() => {
                  updateListState({
                    selectedParentOrg: isActive ? null : chip.key,
                    currentPage: 1,
                  });
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  isActive ? 'bg-brand-600 text-white' : inactiveClasses
                }`}
              >
                🏢 {chip.label} ({chip.count})
              </button>
            );
          })}
        {parentOrgCounts.unset > 0 && (
          <button
            type='button'
            onClick={() => {
              updateListState({
                selectedParentOrg:
                  selectedParentOrg === UNSET_PARENT_ORG_FILTER ? null : UNSET_PARENT_ORG_FILTER,
                currentPage: 1,
              });
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              selectedParentOrg === UNSET_PARENT_ORG_FILTER
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ❓ Unset ({parentOrgCounts.unset})
          </button>
        )}
      </div>

      {/* Header with Search, Filter, and Add Button */}
      <div className='flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center'>
        <div className='relative w-full sm:flex-1'>
          <input
            type='text'
            placeholder='Search brands...'
            value={searchTerm}
            onChange={(e) => updateListState({ searchTerm: e.target.value, currentPage: 1 })}
            className='w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          />
          <svg
            className='absolute left-3 top-2.5 h-5 w-5 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
        </div>
        <div className='flex gap-4 items-center flex-wrap'>
          {/* Only while a query is running — there is no ranking without one. */}
          {relevanceAvailable && (
            <RelevanceSortToggle
              active={relevanceActive}
              fallbackLabel='name'
              onToggle={() =>
                pickSort(relevanceActive ? DEFAULT_BRAND_SORT : RELEVANCE_SORT)
              }
            />
          )}
          <label className='flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer'>
            <input
              type='checkbox'
              checked={hideZeroRefs}
              onChange={(e) => updateListState({ hideZeroRefs: e.target.checked, currentPage: 1 })}
              className='rounded text-brand-600 focus:ring-brand-500'
            />
            Hide 0 refs
          </label>
          <label className='flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer'>
            <input
              type='checkbox'
              checked={shopifyOnly}
              onChange={(e) => updateListState({ shopifyOnly: e.target.checked, currentPage: 1 })}
              className='rounded text-brand-600 focus:ring-brand-500'
            />
            Shopify only
          </label>
          <Button variant='secondary' onClick={() => void handleDownloadCsv()}>Download CSV</Button>
          <Button onClick={() => openCreate()}>+ Add Brand</Button>
        </div>
      </div>

      {/* Brands List */}
      <div className='bg-white shadow rounded-lg overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50 sticky top-0'>
              <tr>
                <th className='px-3 py-3 w-8'>
                  <input
                    type='checkbox'
                    checked={allPageSelected}
                    ref={(el) => { if (el) el.indeterminate = somePageSelected; }}
                    onChange={toggleSelectAll}
                    className='rounded text-brand-600 focus:ring-brand-500 cursor-pointer'
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700'
                  onClick={() => toggleSort('name')}
                >
                  Brand <SortIcon field='name' />
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 w-32'
                  onClick={() => toggleSort('country')}
                >
                  Country <SortIcon field='country' />
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32'>
                  Parent Org
                </th>
                <th className='px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase w-12'>
                  MB
                </th>
                <th
                  title={REFS_TOOLTIP}
                  className='px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 w-20'
                  onClick={() => toggleSort('refs')}
                >
                  Refs <SortIcon field='refs' />
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>
                  Website
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 w-28'
                  onClick={() => toggleSort('lastImportedAt')}
                >
                  Last Import <SortIcon field='lastImportedAt' />
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 w-28'
                  onClick={() => toggleSort('createdAt')}
                >
                  Added <SortIcon field='createdAt' />
                </th>
                <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {pagedBrands.map((brand) => {
                const count = parentRefCounts[brand.id];
                const createdDate = brand.createdAt?.toDate?.();
                const isShopify = brandHasShopifySource(brand, sourcesByBrand);
                const isSquareOnline = brandHasSquareOnlineSource(brand, sourcesByBrand);
                const isWooCommerce = brandHasWooCommerceSource(brand, sourcesByBrand);
                const scraperNames = scraperNamesByBrand.get(brand.id.toLowerCase());
                const isSelected = selectedBrands.has(brand.id);
                return (
                  <tr
                    key={brand.id}
                    className={`cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : isShopify
                        ? 'bg-emerald-50/50 hover:bg-emerald-50'
                        : isSquareOnline
                        ? 'bg-rose-50/50 hover:bg-rose-50'
                        : isWooCommerce
                        ? 'bg-indigo-50/50 hover:bg-indigo-50'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      void ensureFullBrand(brand).then(onSelectBrand);
                    }}
                  >
                    <td className='px-3 py-3' onClick={(e) => toggleSelect(brand.id, e)}>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => {}}
                        className='rounded text-brand-600 focus:ring-brand-500 cursor-pointer'
                      />
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex items-center gap-2'>
                        <div className='flex flex-col'>
                          <span className='text-sm font-medium text-gray-900'>
                            {brand.name}
                          </span>
                          {brand.displayName &&
                            brand.displayName !== brand.name && (
                              <span className='text-xs text-gray-500'>
                                {brand.displayName}
                              </span>
                            )}
                        </div>
                        {isShopify && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700'>
                            Shopify
                          </span>
                        )}
                        {isSquareOnline && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700'>
                            Square Online
                          </span>
                        )}
                        {isWooCommerce && (
                          <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700'>
                            WooCommerce
                          </span>
                        )}
                        {scraperNames && (
                          <span
                            className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700'
                            title={
                              scraperNames.length > 0
                                ? `Scraper registered: ${scraperNames.join(', ')}`
                                : 'Scraper registered'
                            }
                          >
                            ⚡ Scraper
                          </span>
                        )}
                        <StatusBadge status={brand.status} />
                        {(brand.acquisitions?.length ?? 0) > 0 && (
                          <span
                            className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700'
                            title={`Acquisitions: ${brand.acquisitions!.length} acquired/lineage brand(s)`}
                          >
                            ⚘ Acquisitions
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-600'>
                      {brand.country ? (
                        <span>{getCountryDisplay(brand.country)}</span>
                      ) : (
                        <span className='text-gray-400'>-</span>
                      )}
                    </td>
                    <td className='px-4 py-3 text-xs text-gray-600'>
                      {(() => {
                        const resolution = resolveParentOrgForBrand(brand, parentOrgIndex);
                        if (resolution.kind === 'none') {
                          return <span className='text-gray-400'>-</span>;
                        }
                        const { label, matched, title } = resolutionLabel(resolution);
                        return (
                          <span
                            className={`truncate block max-w-[8rem] ${
                              matched ? '' : 'text-amber-700 font-medium'
                            }`}
                            title={title}
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className='px-2 py-3 text-center'>
                      {brand.isMicroBrand && (
                        <span className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700' title='Microbrand'>
                          MB
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <span
                        title={REFS_TOOLTIP}
                        className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium ${
                          count === undefined
                            ? 'bg-gray-100 text-gray-400'
                            : count === 0
                            ? 'bg-red-50 text-red-500'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        {count ?? '...'}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-500'>
                      {brand.mainWebsite ? (
                        <span className='truncate block max-w-xs text-gray-500'>
                          {brand.mainWebsite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </span>
                      ) : (
                        <span className='text-gray-400'>-</span>
                      )}
                    </td>
                    <td className='px-4 py-3 text-xs text-gray-500'>
                      {isShopify ? formatRelativeTime(brand.lastImportedAt) : '-'}
                    </td>
                    <td className='px-4 py-3 text-xs text-gray-500'>
                      {createdDate
                        ? createdDate.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '-'}
                    </td>
                    <td className='px-4 py-3 text-right'>
                      <div className='flex justify-end gap-2'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); void handleEdit(brand); }}
                        >
                          Edit
                        </Button>
                        {/* Always rendered, never hidden. Disabled only on a
                            KNOWN non-zero count; an uncounted brand ("...")
                            stays enabled, because the server re-counts parents
                            AND variants before anything is deleted. The span
                            carries the tooltip: a disabled button fires no
                            hover events of its own. */}
                        <span
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          title={
                            count !== undefined && count > 0
                              ? `Cannot delete — ${count} reference${count !== 1 ? 's' : ''}`
                              : undefined
                          }
                        >
                          <Button
                            variant='ghost'
                            size='sm'
                            disabled={count !== undefined && count > 0}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDeletingBrand(brand); }}
                            className='text-red-500 hover:text-red-700 hover:bg-red-50'
                          >
                            Delete
                          </Button>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagedBrands.length === 0 && (
          <div className='text-center py-8 text-gray-500'>
            {searchTerm ? 'No brands match your search' : 'No brands found'}
          </div>
        )}
      </div>

      {/* Footer: count + page size + pagination */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <p className='text-sm text-gray-500'>
            {processedBrands.length} of {brands.length} brands
          </p>
          <div className='flex items-center gap-1.5'>
            <label className='text-xs text-gray-500'>Show</label>
            <select
              value={pageSize}
              onChange={(e) => updateListState({ pageSize: Number(e.target.value), currentPage: 1 })}
              className='border border-gray-300 rounded px-1.5 py-0.5 text-xs text-gray-600'
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
        {totalPages > 1 && (
          <div className='flex items-center gap-2'>
            <Button
              variant='secondary'
              size='sm'
              disabled={currentPage <= 1}
              onClick={() => updateListState({ currentPage: currentPage - 1 })}
            >
              Previous
            </Button>
            <span className='text-sm text-gray-600'>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant='secondary'
              size='sm'
              disabled={currentPage >= totalPages}
              onClick={() => updateListState({ currentPage: currentPage + 1 })}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Floating bulk action bar */}
      {selectedBrands.size > 0 && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3'>
          <span className='text-sm font-medium text-gray-300'>
            {selectedBrands.size} brand{selectedBrands.size !== 1 ? 's' : ''} selected
          </span>
          <div className='w-px h-5 bg-gray-600' />
          {selectedShopifyBrands.length > 0 && (
            <Button
              size='sm'
              onClick={() => void handleBulkShopifyImport()}
              className='bg-emerald-600 hover:bg-emerald-700 text-white border-0'
            >
              Run Shopify Import ({selectedShopifyBrands.length})
            </Button>
          )}
          <Button
            size='sm'
            onClick={handleBulkMbwdbCheck}
            loading={bulkMbwdbChecking}
            className='bg-violet-600 hover:bg-violet-700 text-white border-0'
          >
            {bulkMbwdbChecking && mbwdbProgress ? mbwdbProgress : `Check MBWDB (${selectedBrands.size})`}
          </Button>
          <Button
            size='sm'
            onClick={handleExportReferences}
            loading={isExporting}
            className='bg-blue-600 hover:bg-blue-700 text-white border-0'
          >
            Export References
          </Button>
          <button
            onClick={clearSelection}
            className='text-gray-400 hover:text-white text-lg leading-none ml-1'
            aria-label='Clear selection'
          >
            ×
          </button>
        </div>
      )}

      <BrandDeleteModal
        brand={deletingBrand}
        onClose={() => setDeletingBrand(null)}
        onDeleted={(brandId) => setBrands((prev) => prev.filter((b) => b.id !== brandId))}
      />

      {/* Brand form — ONE modal for Add and Edit. Create mode has no
          `editingBrand`; after Create Brand it carries on in edit mode on the
          saved brand, which is when the id-bound parts (images, timeline,
          maintenance) appear. */}
      <Modal
        isOpen={isCreating || !!editingBrand}
        onClose={editingBrand ? handleCloseEdit : handleCancelCreate}
        title={editingBrand ? `Edit Brand: ${editingBrand.name}` : 'Add New Brand'}
        size='full'
        headerActions={
          editingBrand ? (
            <CopyLinkButton url={buildShareUrl('brand', editingBrand.id)} noun='brand' />
          ) : undefined
        }
      >
        {(isCreating || editingBrand) && (
          <div className='space-y-4'>
            {editingBrand && activeEditSeed?.brandId === editingBrand.id && (
              <SeedNotice
                seed={activeEditSeed}
                carried={describeCarriedValues(activeEditSeed.values, EDIT_FORM_KEYS)}
                verb='Save'
              />
            )}
            {!editingBrand && activeCreateSeed && (
              <SeedNotice
                seed={activeCreateSeed}
                carried={describeCarriedValues(activeCreateSeed.values, CREATE_FORM_KEYS)}
                verb='Create Brand'
              />
            )}
            <FormSection title='Identity'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Name {!editingBrand && <span className='text-red-500'>*</span>}
                </label>
                <input
                  type='text'
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder='e.g., Rolex'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
                {!editingBrand && (
                  <p className='text-xs text-gray-500 mt-1'>
                    This will be used to generate the brand ID
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Display Name
                </label>
                <input
                  type='text'
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder='Optional display name'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>

              <TagInput
                label='Also Known As'
                values={editAltNames}
                onChange={setEditAltNames}
                placeholder='e.g., International Watch Company'
                disabled={isProcessing}
              />

              <TagInput
                label='Abbreviations (for ads)'
                values={editAbbreviations}
                onChange={(values) =>
                  setEditAbbreviations(values.map((v) => v.toUpperCase()))
                }
                placeholder='e.g., RLX, SUBM, DAYT'
                disabled={isProcessing}
              />

              <div className='mt-2 flex items-center gap-3'>
                <div className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    id='isMicroBrand'
                    checked={!!editIsMicroBrand}
                    onChange={(e) => { setEditIsMicroBrand(e.target.checked); setMbwdbResult(null); }}
                    className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                  />
                  <label htmlFor='isMicroBrand' className='text-sm text-gray-600'>
                    Microbrand
                  </label>
                </div>
                <button
                  type='button'
                  disabled={mbwdbChecking}
                  onClick={async () => {
                    setMbwdbChecking(true);
                    setMbwdbResult(null);
                    try {
                      const brandName = editName.trim() || editDisplayName.trim();
                      const result = await checkMbwdb(brandName);
                      if (result === 'microbrand') {
                        setEditIsMicroBrand(true);
                        setMbwdbResult('found');
                      } else if (result === 'not_microbrand') {
                        setEditIsMicroBrand(false);
                        setMbwdbResult('not_found');
                      } else {
                        setMbwdbResult('not_found');
                      }
                    } catch {
                      setMbwdbResult('not_found');
                    } finally {
                      setMbwdbChecking(false);
                    }
                  }}
                  className='text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors'
                >
                  {mbwdbChecking ? 'Checking...' : 'Check MBWDB'}
                </button>
                {mbwdbResult === 'found' && (
                  <span className='text-xs text-green-600 font-medium'>Microbrand (MBWDB)</span>
                )}
                {mbwdbResult === 'not_found' && (
                  <span className='text-xs text-gray-400'>Not a microbrand / not found</span>
                )}
              </div>

              {/* Dial glyph — text description drives AI brand identification for
                  dials that carry a mark rather than the brand name. The image is
                  captured now but not consumed by extraction yet (Phase 2). */}
              <div className='border rounded-lg p-4 space-y-3'>
                <div className='text-sm font-medium text-gray-700'>
                  Dial Glyph (AI)
                </div>
                <div>
                  <div className='flex items-center justify-between mb-1'>
                    <label className='block text-sm font-medium text-gray-700'>
                      Glyph Description
                    </label>
                    <Button
                      variant='secondary'
                      onClick={handleGenerateGlyph}
                      loading={glyphAiLoading}
                      disabled={isProcessing || aiLoading}
                    >
                      ✨ Generate
                    </Button>
                  </div>
                  <textarea
                    value={editGlyphDescription}
                    onChange={(e) => setEditGlyphDescription(e.target.value)}
                    rows={2}
                    placeholder='e.g., Twin crossed flags looking like +::'
                    className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                  />
                  <p className='mt-1 text-xs'>
                    <span
                      className={
                        editGlyphDescription.length > GLYPH_DESCRIPTION_SOFT_LIMIT
                          ? 'text-amber-600 font-medium'
                          : 'text-gray-400'
                      }
                    >
                      {editGlyphDescription.length} / {GLYPH_DESCRIPTION_SOFT_LIMIT}
                    </span>
                    <span className='text-gray-400'>
                      {' '}
                      · Describe the dial mark for a vision model, not the brand
                      name. Leave empty when the dial only carries the brand name.
                    </span>
                  </p>
                </div>

                {editingBrand ? (
                  <BrandImageUpload
                    brandId={editingBrand.id}
                    variant='glyph'
                    imageUrl={editGlyphImageUrl}
                    onImageUrlChange={setEditGlyphImageUrl}
                    disabled={isProcessing}
                  />
                ) : (
                  <AfterSaveNote>The glyph image can be added once the brand is created.</AfterSaveNote>
                )}
              </div>
            </FormSection>

            <FormSection title='Description'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Description
                </label>
                {/* Rich text → descriptionRich; its plain text is saved to
                    description too, for app builds without the rich renderer. */}
                <RichTextEditor value={editDescription} onChange={setEditDescription} minHeight={120} entityLinks />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  AI Description
                  <span className='ml-2 text-xs text-gray-400 font-normal'>(Used in mobile app)</span>
                </label>
                <textarea
                  value={editAiDescription}
                  onChange={(e) => setEditAiDescription(e.target.value)}
                  rows={4}
                  placeholder='AI-generated brand description for the mobile app...'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>
            </FormSection>

            <FormSection title='Media'>
              {/* Images upload under the brand's id, so they wait for the first
                  save — nothing is stored for a brand that is then cancelled. */}
              {editingBrand ? (
                <BrandImageUpload
                  brandId={editingBrand.id}
                  variant='logo'
                  imageUrl={editLogo}
                  sourceUrl={editLogoSourceUrl}
                  onImageUrlChange={setEditLogo}
                  onSourceUrlChange={setEditLogoSourceUrl}
                />
              ) : (
                <AfterSaveNote>Logo and hero image can be added once the brand is created.</AfterSaveNote>
              )}

              <div>
              {editingBrand && (
                <BrandImageUpload
                  brandId={editingBrand.id}
                  variant='hero'
                  imageUrl={editHeroImage}
                  sourceUrl={editHeroImageSourceUrl}
                  showHeroToggle
                  useHeroImage={editUseHeroImage}
                  onImageUrlChange={setEditHeroImage}
                  onSourceUrlChange={setEditHeroImageSourceUrl}
                  onUseHeroImageChange={setEditUseHeroImage}
                />
              )}
              <div className='mt-2'>
                <div className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    id='logoHeroTransparent'
                    checked={editLogoHeroTransparent}
                    onChange={(e) => setEditLogoHeroTransparent(e.target.checked)}
                    className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                  />
                  <label htmlFor='logoHeroTransparent' className='text-sm text-gray-600'>
                    Transparent logo over hero image
                  </label>
                </div>
                <p className='mt-1 ml-6 text-xs text-gray-500'>
                  Removes the white background card behind the logo. Only enable if the logo asset has transparency.
                </p>
              </div>
              </div>

                <div className='flex items-center gap-2'>
                  <input type='checkbox' id='hideImages' checked={editHideImages} onChange={(e) => setEditHideImages(e.target.checked)} className='rounded border-amber-300 text-amber-600 focus:ring-amber-500' />
                  <label htmlFor='hideImages' className='text-sm text-amber-600'>Hide images</label>
                </div>
            </FormSection>

            <FormSection title='Location'>
              <CountrySelect value={editCountry} onChange={setEditCountry} />

              <BrandLocationFields value={editLocation} onChange={setEditLocation} />

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Address
                </label>
                <textarea
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  rows={3}
                  placeholder='Company address...'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>
            </FormSection>

            <FormSection title='History & Status'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Founded Year
                </label>
                <input
                  type='number'
                  value={editFounded}
                  onChange={(e) => setEditFounded(e.target.value)}
                  placeholder='e.g., 1905'
                  min='1700'
                  max={new Date().getFullYear()}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>

              <BrandIdentityFields
                part='lifecycle'
                value={editIdentity}
                onChange={setEditIdentity}
                status={editStatus}
                onStatusChange={setEditStatus}
              />

              {(editStatus === 'defunct' ||
                editStatus === 'dormant' ||
                editStatus === 'absorbed') && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Year Ceased
                  </label>
                  <input
                    type='number'
                    value={editYearCeased ?? ''}
                    onChange={(e) =>
                      setEditYearCeased(e.target.value ? Number(e.target.value) : undefined)
                    }
                    placeholder='e.g., 1988'
                    min='1700'
                    max={new Date().getFullYear()}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                  />
                  <p className='text-xs text-gray-500 mt-1'>
                    Shown for defunct, dormant &amp; absorbed brands. Leave blank if
                    unknown.
                  </p>
                </div>
              )}

              <BrandCommerceFields part='founder' value={editCommerce} onChange={setEditCommerce} />

              {/* Timeline. Its writes are transactional and independent of this
                  form's Save — the brand draft carries no `history`, so routing
                  it through Save is what silently dropped a curator's event on
                  the manufacturer side. */}
              {editingBrand ? (
                <BrandTimelineSection
                  brand={editingBrand}
                  onBrandChange={(next) => setEditingBrand(next)}
                />
              ) : (
                <AfterSaveNote>The timeline can be added once the brand is created.</AfterSaveNote>
              )}
            </FormSection>

            <FormSection title='Ownership & Relationships'>
              <BrandIdentityFields part='ownership' value={editIdentity} onChange={setEditIdentity} status={editStatus} />

              <div>
                <ParentOrgPicker
                  label='Parent Organization'
                  value={{ parentOrgId: editParentOrgId, parentOrg: editParentOrg }}
                  index={parentOrgIndex}
                  loading={parentOrgsLoading}
                  error={parentOrgsError}
                  onChange={(next) => {
                    setEditParentOrgId(next.parentOrgId);
                    setEditParentOrg(next.parentOrg);
                  }}
                />
                <SiblingBrandsHint
                  parentOrg={{ parentOrgId: editParentOrgId, parentOrg: editParentOrg }}
                  brands={brands}
                  excludeId={editingBrand?.id}
                  index={parentOrgIndex}
                />
              </div>

              {/* Acquisitions / lineage — any brand selectable (no defunct gate). */}
              <AcquisitionsEditor
                value={editAcquisitions}
                onChange={setEditAcquisitions}
                disabled={isProcessing}
                brandOptions={brands
                  .filter((b) => b.id !== editingBrand?.id)
                  .map((b) => ({
                    id: b.id,
                    name: b.displayName || b.name,
                    subtitle: b.id,
                  }))}
              />
              {editStatus !== 'defunct' && editingBrand && (() => {
                const acquiredBy = brands.filter(
                  (b) =>
                    b.id !== editingBrand.id &&
                    b.acquisitions?.some(
                      (a) => a.acquiredBrandId === editingBrand.id,
                    ),
                );
                return acquiredBy.length > 0 ? (
                  <p className='w-full text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1'>
                    ⚠ {acquiredBy.length} brand{acquiredBy.length === 1 ? '' : 's'} list this as an acquisition
                    ({acquiredBy.map((b) => b.displayName || b.name).join(', ')}).
                  </p>
                ) : null;
              })()}
            </FormSection>

            <FormSection title='Commerce & Links'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Website
                </label>
                <input
                  type='url'
                  value={editWebsite}
                  onChange={(e) => setEditWebsite(e.target.value)}
                  placeholder='https://...'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>

              <BrandCommerceFields part='storefront' value={editCommerce} onChange={setEditCommerce} />

                <div className='flex items-center gap-2'>
                  <input type='checkbox' id='showProductLinks' checked={editShowProductLinks} onChange={(e) => setEditShowProductLinks(e.target.checked)} className='rounded border-gray-300 text-brand-600 focus:ring-brand-500' />
                  <label htmlFor='showProductLinks' className='text-sm text-gray-600'>Show product links</label>
                </div>
            </FormSection>

            <FormSection title='Catalogue'>
              <LookupMultiSelect
                label='Winding Directions'
                values={editWindingDirections}
                onChange={setEditWindingDirections}
                lookupCollection='lookup_winding_directions'
              />

              {/* Dedup keying — which field the Reference Cleanup tool clusters on */}
              <div className='mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200'>
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>Dedup keying</p>
                <div className='flex items-center gap-2'>
                  <label htmlFor='dedupIdentityField' className='text-sm text-gray-600'>Identity field</label>
                  <select
                    id='dedupIdentityField'
                    value={editDedupIdentityField}
                    onChange={(e) => setEditDedupIdentityField(e.target.value as 'reference' | 'model')}
                    className='border border-gray-300 rounded px-2 py-1 text-sm'
                  >
                    <option value='reference'>Reference (default)</option>
                    <option value='model'>Model</option>
                  </select>
                </div>
                <p className='mt-2 text-xs text-gray-500'>
                  For brands where multiple distinct watches share a caseback marking
                  (e.g. Seiko's <code className='px-1 bg-gray-100 rounded'>4R36-01B0</code> used across SRP263/SRP785/SRP383),
                  pick <strong>Model</strong> so dedup keys on the unique model SKU instead of the shared reference.
                </p>
              </div>
            </FormSection>

            <FormSection title='Visibility & Admin'>
              <div className='flex flex-wrap items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <input type='checkbox' id='isPromoted' checked={editIsPromoted} onChange={(e) => setEditIsPromoted(e.target.checked)} className='rounded border-gray-300 text-brand-600 focus:ring-brand-500' />
                  <label htmlFor='isPromoted' className='text-sm text-gray-600'>Promoted</label>
                </div>
                {editIsPromoted && (
                  <div className='flex items-center gap-1'>
                    <label className='text-xs text-gray-500'>Order:</label>
                    <input type='number' value={editPromotedOrder} onChange={(e) => setEditPromotedOrder(parseInt(e.target.value) || 0)} className='w-16 border border-gray-300 rounded px-2 py-1 text-sm' min={0} />
                  </div>
                )}
                <div className='flex items-center gap-2'>
                  <input type='checkbox' id='isDisabled' checked={editIsDisabled} onChange={(e) => setEditIsDisabled(e.target.checked)} className='rounded border-red-300 text-red-600 focus:ring-red-500' />
                  <label htmlFor='isDisabled' className='text-sm text-red-600'>Disabled (hidden from Explore)</label>
                </div>
                <div className='flex items-center gap-2'>
                  <input type='checkbox' id='needsReview' checked={editNeedsReview} onChange={(e) => setEditNeedsReview(e.target.checked)} className='rounded border-purple-300 text-purple-600 focus:ring-purple-500' />
                  <label htmlFor='needsReview' className='text-sm text-purple-700' title='Audit worklist flag — set when this brand needs manual lineage/status verification'>Needs review</label>
                </div>
              </div>

              {/* Curator documentation — Confluence pages surfaced at the calibre
                  surfaces where this brand's conventions actually get applied. */}
              <DocsLinksEditor
                value={editDocs}
                onChange={setEditDocs}
                disabled={isProcessing}
                entityNoun='brand'
              />
            </FormSection>

            <FormSection title='Legacy' collapsible description='Older free-text field, kept for app builds that still read it. Hints for AI enrich go in the Enrich with AI dialog.'>
              <div>
                <label htmlFor='brandInformation' className='block text-sm font-medium text-gray-700 mb-1'>
                  Information
                </label>
                <textarea
                  id='brandInformation'
                  value={editInfo}
                  onChange={(e) => setEditInfo(e.target.value)}
                  rows={4}
                  placeholder='Brand description and information...'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>
            </FormSection>

            <div className='flex justify-between items-center pt-4 border-t'>
              <Button
                variant='secondary'
                onClick={() => {
                  if (!editName.trim()) {
                    toast.error('Enter a brand name before enriching');
                    return;
                  }
                  setShowEnrichHint(true);
                }}
                loading={aiLoading}
                disabled={isProcessing || glyphAiLoading}
              >
                ✨ Enrich with AI
              </Button>
              <div className='flex gap-3'>
                <Button
                  variant='secondary'
                  onClick={editingBrand ? handleCloseEdit : handleCancelCreate}
                  disabled={isProcessing || aiLoading || glyphAiLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={editingBrand ? handleSave : handleCreate}
                  loading={isProcessing}
                  disabled={aiLoading || glyphAiLoading}
                >
                  {editingBrand ? 'Save' : 'Create Brand'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <EnrichHintDialog
        isOpen={showEnrichHint}
        entityNoun='brand'
        descriptionLinkCount={countEntityLinks(editDescription)}
        hint={enrichHint}
        onHintChange={setEnrichHint}
        onCancel={() => setShowEnrichHint(false)}
        onRun={() => {
          setShowEnrichHint(false);
          void handleAIEnrich(enrichHint);
        }}
      />

      {/* AI Suggestions Modal (create and edit) */}
      <Modal
        isOpen={showAISuggestions}
        onClose={() => setShowAISuggestions(false)}
        title='AI Suggestions'
        size='xl'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Review Claude's suggestions below. Check the rows you want to apply,
            then click <span className='font-medium'>Apply Selected</span>.
          </p>
          <div className='border rounded-lg overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-3 py-2 w-12'></th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Field
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Current
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Suggested
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-24'>
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {Object.entries(aiSuggestions).map(([key, value]) => {
                  const handler = aiFieldHandlers[key];
                  const label = handler?.label ?? key;
                  const current = handler?.currentDisplay ?? '';
                  const suggested = handler?.formatSuggested
                    ? handler.formatSuggested(value)
                    : typeof value === 'object'
                      ? JSON.stringify(value)
                      : typeof value === 'boolean'
                      ? (value ? 'Yes' : 'No')
                      : String(value);
                  const confidence = aiConfidence[key] || 'medium';
                  const badgeVariant: 'success' | 'warning' | 'default' =
                    confidence === 'high'
                      ? 'success'
                      : confidence === 'medium'
                      ? 'warning'
                      : 'default';
                  const unsupported = !handler;
                  return (
                    <tr
                      key={key}
                      className={unsupported ? 'opacity-50' : ''}
                    >
                      <td className='px-3 py-2 text-center'>
                        <input
                          type='checkbox'
                          checked={!!aiAccepted[key]}
                          disabled={unsupported}
                          onChange={(e) =>
                            setAiAccepted((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                          className='rounded border-gray-300'
                        />
                      </td>
                      <td className='px-3 py-2 font-medium text-gray-900'>
                        {label}
                        {unsupported && (
                          <span className='ml-2 text-xs text-gray-400'>
                            (not editable here)
                          </span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-gray-500 max-w-xs truncate'>
                        {current || (
                          <span className='italic text-gray-400'>empty</span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-gray-900 max-w-md whitespace-pre-wrap break-words'>
                        {suggested}
                      </td>
                      <td className='px-3 py-2'>
                        <Badge variant={badgeVariant}>{confidence}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className='flex justify-end gap-2 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setShowAISuggestions(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApplyAISuggestions}>
              Apply Selected
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

/**
 * Banner on a modal opened from an external-catalogue seed. The seeded values
 * the form has inputs for are visible in those inputs; this lists the rest, so
 * nothing is written that the reviewer could not see.
 */
function SeedNotice({
  seed,
  carried,
  verb,
}: {
  seed: CreateBrandSeed | EditBrandPatchSeed;
  carried: { key: string; label: string; display: string }[];
  verb: string;
}) {
  return (
    <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
      <p className='font-medium'>Pre-filled from {seed.sourceLabel}</p>
      <p className='text-blue-800'>
        Review the fields below — {verb} writes them along with a provenance stamp for each
        imported field.
      </p>
      {carried.length > 0 && (
        <>
          <p className='mt-2 text-blue-800'>Also written, with no field on this form:</p>
          <dl className='mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5'>
            {carried.map((item) => (
              <div key={item.key} className='contents'>
                <dt className='text-blue-700'>{item.label}</dt>
                <dd className='break-all'>{item.display}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </div>
  );
}

function StatPill({
  label,
  value,
  accent,
  title,
}: {
  label: string;
  value: number | string;
  accent?: 'emerald' | 'blue';
  /** Optional hover explanation — e.g. what a count does and doesn't include. */
  title?: string;
}) {
  const accentClass =
    accent === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : accent === 'blue'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <div
      title={title}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${accentClass}`}
    >
      <span className='font-semibold tabular-nums'>{value}</span>
      <span className='text-opacity-75'>{label}</span>
    </div>
  );
}

/**
 * "Other Swatch Group brands: …" under the parent-org picker.
 *
 * Siblings are brands resolving to the same ORGANIZATION, not brands holding the
 * same string — so `Rolex SA` and `rolex_sa` brands list each other, which the
 * old raw-string comparison never did.
 */
function SiblingBrandsHint({
  parentOrg,
  brands,
  excludeId,
  index,
}: {
  parentOrg: { parentOrgId: string; parentOrg: string };
  brands: WatchBrand[];
  excludeId?: string;
  index: ParentOrgIndex;
}) {
  const org = resolvedOrg(resolveParentOrgForBrand(parentOrg, index));
  if (!org) return null;
  const siblings = brands.filter(
    (b) => b.id !== excludeId && resolvedOrg(resolveParentOrgForBrand(b, index))?.id === org.id,
  );
  if (siblings.length === 0) return null;
  return (
    <p className='text-xs text-gray-500 mt-1'>
      Other {orgDisplayName(org)} brands: {siblings.map((b) => b.name).join(', ')}
    </p>
  );
}
